import { Municipality } from "@/common/articleTypes";
import { AIClient } from "@/ai/AIAssistant";
import {
  downloadFile,
  getFilenameFromHeaders,
  HttpStatus,
  isBadRequestError,
  isHttpError,
} from "@/api";
import { downloadDocument } from "@/api/DownloadService";
import { DestinationFoldersResponse } from "@/archive/archiveDomain";
import {
  CustomizedFreetextType,
  failure,
  GetProjectDocumentRecipientsResponse,
  GetProjectDocumentSignersResponse,
  isOk,
  isPending,
  LocalizedTextForDocument,
  ok,
  pending,
  Result,
  SubscriptionTypeModel,
} from "@/common/common";
import { DownloadAsPdfButtonProps } from "@/common/commonComponents";
import { StringFieldModel } from "@/common/commonForms";
import { DecisionType } from "@/common/decisions";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import { ModuleName } from "@/common/product";
import { PaidSubscription, SubscriptionType } from "@/common/subscription";
import { DocumentPreviewProps } from "@/components-v2/DocumentPreview";
import { DropdownMenuItems } from "@/components-v2/Dropdown";
import { FilePreviewProps } from "@/components-v2/FilePreview";
import { SelectProps } from "@/components-v2/Select";
import { TagProps } from "@/components-v2/Tag";
import {
  AcquireOwnSharesPowerOfAttorney,
  AcquireOwnSharesPowerOfAttorneyForm,
} from "@/dbot/acquireOwnSharesPowerOfAttorney/acquireOwnSharesPowerOfAttorneyDomain";
import { acquireOwnSharesPowerOfAttorneyHandler } from "@/dbot/acquireOwnSharesPowerOfAttorney/acquireOwnSharesPowerOfAttorneyHandler";
import {
  ApproveAnnualAccount,
  ApproveAnnualAccountForm,
} from "@/dbot/approveAnnualAccount/approveAnnualAccountDomain";
import { approveAnnualAccountHandler } from "@/dbot/approveAnnualAccount/approveAnnualAccountHandler";
import {
  ApproveAuditorRemuneration,
  ApproveAuditorRemunerationForm,
} from "@/dbot/approveAuditorRemuneration/approveAuditorRemunerationDomain";
import { approveAuditorRemunerationHandler } from "@/dbot/approveAuditorRemuneration/approveAuditorRemunerationHandler";
import {
  ApproveCompanyBoardRemuneration,
  ApproveCompanyBoardRemunerationForm,
} from "@/dbot/approveCompanyBoardRemuneration/approveCompanyBoardRemunerationDomain";
import { approveCompanyBoardRemunerationHandler } from "@/dbot/approveCompanyBoardRemuneration/approveCompanyBoardRemunerationHandler";
import {
  ApproveNominationCommitteeRemuneration,
  ApproveNominationCommitteeRemunerationForm,
} from "@/dbot/approveNominationCommitteeRemuneration/approveNominationCommitteeRemunerationDomain";
import { approveNominationCommitteeRemunerationHandler } from "@/dbot/approveNominationCommitteeRemuneration/approveNominationCommitteeRemunerationHandler";
import {
  ArticlesOfAssociation,
  ArticlesOfAssociationForm,
} from "@/dbot/articlesofassociation/articlesofassociationDomain";
import { articlesOfAssociationHandler } from "@/dbot/articlesofassociation/articlesOfAssociationHandler";
import {
  AssignsNewCompanyBoard,
  AssignsNewCompanyBoardForm,
} from "@/dbot/assignsNewCompanyBoard/assignsNewCompanyBoardDomain";
import { assignsNewCompanyBoardHandler } from "@/dbot/assignsNewCompanyBoard/assignsNewCompanyBoardHandler";
import {
  deleteAttachment,
  keepOriginalFormat,
  downloadAttachment,
  triggerConversionStatus,
  uploadAttachments,
  UploadedAttachmentProps,
  uploadingAttachments,
} from "@/dbot/attachments";
import { ChangeOfAuditor, ChangeOfAuditorForm } from "@/dbot/auditor/auditorDomain";
import { auditorHandler } from "@/dbot/auditor/auditorHandler";
import { ChangeOfAccountant, ChangeOfAccountantForm } from "@/dbot/accountant/accountantDomain";
import { accountantHandler } from "@/dbot/accountant/accountantHandler";
import {
  BoardAssignsSignature,
  BoardAssignsSignatureForm,
} from "@/dbot/boardAssignsSignature/boardAssignsSignatureDomain";
import { boardAssignsSignatureHandler } from "@/dbot/boardAssignsSignature/boardAssignsSignatureHandler";
import { BoardMeetingMinutes } from "@/dbot/boardMeetingMinutes/boardMeetingMinutesDomain";
import { BoardMeetingMinutesForm } from "@/dbot/boardMeetingMinutes/BoardMeetingMinutesForm";
import { boardMeetingMinutesHandler } from "@/dbot/boardMeetingMinutes/boardMeetingMinutesHandler";
import { BoardMeetingVote } from "@/dbot/boardMeetingMinutes/boardMeetingVoting";
import {
  CapitalIncreaseThroughBonusIssue,
  CapitalIncreaseThroughBonusIssueForm,
} from "@/dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueDomain";
import { capitalIncreaseThroughBonusIssueHandler } from "@/dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueHandler";
import {
  ChangeArticlesOfAssociation,
  ChangeArticlesOfAssociationForm,
} from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationDomain";
import { changeArticlesOfAssociationHandler } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationHandler";
import {
  ChangeBusinessPurpose,
  ChangeBusinessPurposeForm,
} from "@/dbot/changeBusinessPurpose/changeBusinessPurposeDomain";
import { changeBusinessPurposeHandler } from "@/dbot/changeBusinessPurpose/changeBusinessPurposeHandler";
import { ChangeOfAddress, ChangeOfAddressForm } from "@/dbot/changeOfAddress/changeOfAddressDomain";
import { changeOfAddressHandler } from "@/dbot/changeOfAddress/changeOfAddressHandler";
import {
  ChangeOfBusinessManager,
  ChangeOfBusinessManagerForm,
} from "@/dbot/changeOfBusinessManager/changeOfBusinessManagerDomain";
import { changeOfBusinessManagerHandler } from "@/dbot/changeOfBusinessManager/changeOfBusinessManagerHandler";
import {
  ChangeCompanyBoardInArticlesOfAssociation,
  ChangeCompanyBoardInArticlesOfAssociationForm,
} from "@/dbot/changeCompanyBoardInArticlesOfAssociation/changeCompanyBoardInArticlesOfAssociationDomain";
import { changeCompanyBoardInArticlesOfAssociationHandler } from "@/dbot/changeCompanyBoardInArticlesOfAssociation/changeCompanyBoardInArticlesOfAssociationHandler";
import {
  ChangeSignatureInArticlesOfAssociation,
  ChangeSignatureInArticlesOfAssociationForm,
} from "@/dbot/changeSignatureInArticlesOfAssociation/changeSignatureInArticlesOfAssociationDomain";
import { changeSignatureInArticlesOfAssociationHandler } from "@/dbot/changeSignatureInArticlesOfAssociation/changeSignatureInArticlesOfAssociationHandler";
import {
  BoardMeeting,
  BoardMeetingFields,
  createBoardMeetingFields,
  prepareBoardMeetingForSave,
  toRecipients,
} from "@/dbot/common/commonBoardMeetingDomain";
import {
  anyDocumentSentForSigning,
  applyCompanyRoleEmails,
  applyDecisionFailureEvent,
  applyDecisionSuccessEvent,
  applyShareholderEmail,
  AttachmentPreview,
  AutoAttachmentType,
  BatchDocumentOverrrides,
  categoryMapping,
  categorySubscriptionMapping,
  CleanupFunction,
  CompanyRoleIdWithName,
  ConfirmRemoveDocumentEvent,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  DecisionItem,
  DecisionStatusResponse,
  DecisionTypeHandler,
  defaultDocumentLanguage,
  DeleteDocumentBlobEvent,
  DetailsPanelRef,
  DocumentContext,
  DocumentItem,
  DocumentPreview,
  DocumentTypeHandler,
  DocumentViewingStatus,
  checkCompanyNameResultEvent,
  CompanyNameCheck,
  checkProviderValidityResultEvent,
  ProviderValidity,
  checkIncorporationStatusEvent,
  checkIncorporationStatusFailureEvent,
  checkIncorporationStatusSuccessEvent,
  createMergerCounterpartFailureEvent,
  createMergerCounterpartSuccessEvent,
  fetchIncorporationActivationInfoFailureEvent,
  fetchIncorporationActivationInfoSuccessEvent,
  DseStatusResponse,
  DsePrekontrollErrorItem,
  dsePrekontrollFailureMessage,
  dsePrekontrollFailureMessageWithDecisionLink,
  dseSubmitErrorMessage,
  isDseSystemUnavailable,
  parseDseSubmitAck,
  prekontrollIncorporationEvent,
  prekontrollIncorporationFailureEvent,
  prekontrollIncorporationSuccessEvent,
  Evaluation,
  fetchDecisionStatusEvent,
  skipSendToBRResultEvent,
  fetchDecisionStatusResultEvent,
  fetchIncreaseOfShareCapitalSubscribersAllocationFailureEvent,
  fetchIncreaseOfShareCapitalSubscribersAllocationSuccessEvent,
  fetchVotingResultEvent,
  findStateForDecision,
  FollowUpType,
  Freetext,
  GeneralMeetingParticipation,
  getDecisionActionLabel,
  getDecisionName,
  GetDocumentViewingStatusResponse,
  GetProjectResponse,
  getSigningStatusFailureEvent,
  getSigningStatusSuccessEvent,
  getSubscriptionStatusFailureEvent,
  getSubscriptionStatusSuccessEvent,
  getViewingStatusFailureEvent,
  getViewingStatusSuccessEvent,
  InputState,
  isAoAUpdateInProgress,
  isDbotEvent,
  isDocumentLocked,
  isDocumentSent,
  isDocumentSentForReview,
  isDocumentSentForSigning,
  isDocumentSentForVoting,
  GetSignableDocumentsResponse,
  isDocumentSigned,
  isRemoveDecision,
  isRemovingDecision,
  mapToSendForSigningRequest,
  mapToSendMultipleForSigningRequest,
  MatrikkelAddress,
  MatrikkelAddressVerification,
  NACE_SEARCH_MIN_QUERY_LENGTH,
  NaceSearchResult,
  onCustomDocumentCreated,
  OpenAttachmentPreviewEvent,
  OpenDocumentBlobEvent,
  PowerOfAttorneyForGeneralMeeting,
  ProjectCompany,
  DissolutionStatus,
  ProjectContext,
  ProjectControllerState,
  ProjectDocumentStatus,
  ProjectInitializationParams,
  ProjectJsonData,
  ProjectsForCopyResponse,
  ProjectStatus,
  readyState,
  Recipient,
  RecipientId,
  refreshProjectStatusEvent,
  refreshProjectStatusFailureEvent,
  refreshProjectStatusSuccessEvent,
  registerNewSignerFailureEvent,
  registerNewSignerSuccessEvent,
  removeDecisionState,
  removingDecisionState,
  reopenDocumentResultEvent,
  resendNoticeOfBoardMeetingFailureEvent,
  resendNoticeOfBoardMeetingRequest,
  resendNoticeOfBoardMeetingSuccessEvent,
  saveGeneralMeetingVotesErrorEvent,
  saveGeneralMeetingVotesSuccessEvent,
  saveShareAllocationFailureEvent,
  saveShareAllocationSuccessEvent,
  searchCompaniesResultEvent,
  searchMatrikkelAddressResultEvent,
  searchNaceResultEvent,
  searchMunicipalitiesResultEvent,
  sendDocumentForSigningResultEvent,
  sendDocumentForViewingFailureEvent,
  sendDocumentForViewingSuccessEvent,
  sendDocumentsForViewingRequest,
  sendMultipleDocumentsForSigningResultEvent,
  sendSubscriptionFormForSigningResult,
  sendToBRFailureEvent,
  SendToBRFollowUp,
  sendToBRSuccessEvent,
  ShareAllocationRoundsResponse,
  sortedCompanies,
  TabId,
  UpdateBoardMeetingRecipientItem,
  updateBoardMeetingRecipientsFailureEvent,
  updateBoardMeetingRecipientsSuccessEvent,
  updateCompanyRoleEmailsFailureEvent,
  updateCompanyRoleEmailsSuccessEvent,
  UpdateCompanyRoleItem,
  UpdateDocumentPreview,
  updateFollowUpStatusResultEvent,
  updateInputStateEvent,
  UpdateUploadingAttachmentPropsEvent,
  UploadingAttachment,
  verifyMatrikkelAddressResultEvent,
  shareholdersWithSharesExcludingSelf,
  projectTemplateItems,
} from "@/dbot/common/commonDomain";
import {
  createGeneralMeetingFields,
  GeneralMeeting,
  GeneralMeetingFields,
  GeneralMeetingMinutes,
  GeneralMeetingVotingType,
  NoticeToGeneralMeeting,
  prepareGeneralMeetingForSave,
} from "@/dbot/common/commonGeneralMeetingDomain";
import {
  CompanyNameChange,
  CompanyNameChangeForm,
} from "@/dbot/companyNameChange/companyNameChangeDomain";
import { companyNameHandler } from "@/dbot/companyNameChange/companyNameHandler";
import {
  ConversionOfShares,
  ConversionOfSharesForm,
} from "@/dbot/conversionOfShares/conversionOfSharesDomain";
import { conversionOfSharesHandler } from "@/dbot/conversionOfShares/conversionOfSharesHandler";
import {
  ConvertibleLoanPowerOfAttorney,
  ConvertibleLoanPowerOfAttorneyForm,
} from "@/dbot/convertibleLoanPoA/convertibleLoanPoADomain";
import { convertibleLoanPoAHandler } from "@/dbot/convertibleLoanPoA/convertibleLoanPoAHandler";
import {
  deleteDocumentBlob,
  uploadCustomDocumentBlob,
} from "@/dbot/customDocument/customDocumentBlob";
import {
  CustomDocumentData,
  CustomDocumentForm,
  CustomDocumentInitialRecipients,
  CustomDocumentOverrides,
  CustomDocumentType,
} from "@/dbot/customDocument/customDocumentDomain";
import { customDocumentHandler } from "@/dbot/customDocument/customDocumentHandler";
import { Dividend, DividendForm } from "@/dbot/dividend/dividendDomain";
import { dividendHandler } from "@/dbot/dividend/dividendHandler";
import {
  DividendPowerOfAttorney,
  DividendPowerOfAttorneyForm,
} from "@/dbot/dividendPowerOfAttorney/dividendPowerOfAttorneyDomain";
import { dividendPowerOfAttorneyHandler } from "@/dbot/dividendPowerOfAttorney/dividendPowerOfAttorneyHandler";
import { showDuplicateProjectDialog } from "@/dbot/duplicateProject/duplicateProjectDomain";
import {
  ElectionOfCompanyBoard,
  ElectionOfCompanyBoardForm,
} from "@/dbot/ElectionOfCompanyBoard/electionOfCompanyBoardDomain";
import { electionOfCompanyBoardHandler } from "@/dbot/ElectionOfCompanyBoard/electionOfCompanyBoardHandler";
import {
  ElectNominationCommittee,
  ElectNominationCommitteeForm,
} from "@/dbot/electNominationCommittee/electNominationCommitteeDomain";
import { electNominationCommitteeHandler } from "@/dbot/electNominationCommittee/electNominationCommitteeHandler";
import {
  EmploymentOfManagingDirector,
  EmploymentOfManagingDirectorForm,
} from "@/dbot/employmentOfManagingDirector/employmentOfManagingDirectorDomain";
import { employmentOfManagingDirectorHandler } from "@/dbot/employmentOfManagingDirector/employmentOfManagingDirectorHandler";
import { FreetextForm, showFreetext } from "@/dbot/freetext/freetextDomain";
import { freetextHandler } from "@/dbot/freetext/freetextHandler";
import { GeneralMeetingMinutesForm } from "@/dbot/generalMeetingMinutes/generalMeetingMinutesDomain";
import { generalMeetingMinutesHandler } from "@/dbot/generalMeetingMinutes/generalMeetingMinutesHandler";
import {
  GroupContributionByGeneralMeeting,
  GroupContributionByGeneralMeetingForm,
} from "@/dbot/groupContributionByGeneralMeeting/groupContributionByGeneralMeetingDomain";
import { groupContributionByGeneralMeetingHandler } from "@/dbot/groupContributionByGeneralMeeting/groupContributionByGeneralMeetingHandler";
import {
  IncreaseOfShareCapital,
  IncreaseOfShareCapitalForm,
} from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalDomain";
import { increaseOfShareCapitalHandler } from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalHandler";
import {
  IncreaseShareCapitalPowerOfAttorney,
  IncreaseShareCapitalPowerOfAttorneyForm,
} from "@/dbot/increaseShareCapitalPowerOfAttorney/increaseShareCapitalPowerOfAttorneyDomain";
import { increaseShareCapitalPowerOfAttorneyHandler } from "@/dbot/increaseShareCapitalPowerOfAttorney/increaseShareCapitalPowerOfAttorneyHandler";
import {
  InsertCompanyBoardInArticlesOfAssociation,
  InsertCompanyBoardInArticlesOfAssociationForm,
} from "@/dbot/insertCompanyBoardInArticlesOfAssociation/insertCompanyBoardInArticlesOfAssociationDomain";
import { insertCompanyBoardInArticlesOfAssociationHandler } from "@/dbot/insertCompanyBoardInArticlesOfAssociation/insertCompanyBoardInArticlesOfAssociationHandler";
import {
  InsertSignatureInArticlesOfAssociation,
  InsertSignatureInArticlesOfAssociationForm,
} from "@/dbot/insertSignatureInArticlesOfAssociation/insertSignatureInArticlesOfAssociationDomain";
import { insertSignatureInArticlesOfAssociationHandler } from "@/dbot/insertSignatureInArticlesOfAssociation/insertSignatureInArticlesOfAssociationHandler";
import {
  InterimBalanceSheet,
  InterimBalanceSheetForm,
} from "@/dbot/interimBalanceSheet/interimBalanceSheetDomain";
import { interimBalanceSheetHandler } from "@/dbot/interimBalanceSheet/interimBalanceSheetHandler";
import { DissolveCompany, DissolveCompanyForm } from "@/dbot/dissolveCompany/dissolveCompanyDomain";
import { dissolveCompanyHandler } from "@/dbot/dissolveCompany/dissolveCompanyHandler";
import {
  LiquidateCompany,
  LiquidateCompanyForm,
} from "@/dbot/liquidateCompany/liquidateCompanyDomain";
import { liquidateCompanyHandler } from "@/dbot/liquidateCompany/liquidateCompanyHandler";
import { findMergerWithCounterpart, Merger, MergerForm } from "@/dbot/merger/mergerDomain";
import { mergerHandler } from "@/dbot/merger/mergerHandler";
import { NoAuditor, NoAuditorForm } from "@/dbot/noAuditor/noAuditorDomain";
import { noAuditorHandler } from "@/dbot/noAuditor/noAuditorHandler";
import { NoAccountant, NoAccountantForm } from "@/dbot/noAccountant/noAccountantDomain";
import { noAccountantHandler } from "@/dbot/noAccountant/noAccountantHandler";
import {
  NoManagingDirector,
  NoManagingDirectorForm,
} from "@/dbot/noManagingDirector/noManagingDirectorDomain";
import { noManagingDirectorHandler } from "@/dbot/noManagingDirector/noManagingDirectorHandler";
import {
  ShareTransferConsent,
  ShareTransferConsentForm,
} from "@/dbot/shareTransferConsent/shareTransferConsentDomain";
import { shareTransferConsentHandler } from "@/dbot/shareTransferConsent/shareTransferConsentHandler";
import {
  OptionGrantLetter,
  OptionGrantLetterForm,
} from "@/dbot/optionGrantLetter/optionGrantLetterDomain";
import { optionGrantLetterHandler } from "@/dbot/optionGrantLetter/optionGrantLetterHandler";
import {
  exerciseApplyErrorMessage,
  OptionExercise,
  OptionExerciseForm,
} from "@/dbot/optionExercise/optionExerciseDomain";
import { optionExerciseHandler } from "@/dbot/optionExercise/optionExerciseHandler";
import {
  NoBusinessManager,
  NoBusinessManagerForm,
} from "@/dbot/noBusinessManager/noBusinessManagerDomain";
import { noBusinessManagerHandler } from "@/dbot/noBusinessManager/noBusinessManagerHandler";
import {
  NoticeOfBoardMeeting,
  NoticeOfBoardMeetingForm,
} from "@/dbot/noticeBoardMeeting/noticeBoardMeetingDomain";
import { noticeBoardMeetingHandler } from "@/dbot/noticeBoardMeeting/noticeBoardMeetingHandler";
import { NoticeToGeneralMeetingForm } from "@/dbot/noticeGeneralMeeting/noticeGeneralMeetingDomain";
import { noticeGeneralMeetingHandler } from "@/dbot/noticeGeneralMeeting/noticeGeneralMeetingHandler";
import { Procura, ProcuraForm } from "@/dbot/procura/procuraDomain";
import { procuraHandler } from "@/dbot/procura/procuraHandler";
import {
  ReductionOfShareCapital,
  ReductionOfShareCapitalForm,
} from "@/dbot/reductionOfShareCapital/reductionOfShareCapitalDomain";
import { reductionOfShareCapitalHandler } from "@/dbot/reductionOfShareCapital/reductionOfShareCapitalHandler";
import {
  RemoveCompanyBoardFromArticlesOfAssociation,
  RemoveCompanyBoardFromArticlesOfAssociationForm,
} from "@/dbot/removeCompanyBoardFromArticlesOfAssociation/removeCompanyBoardFromArticlesOfAssociationDomain";
import { removeCompanyBoardFromArticlesOfAssociationHandler } from "@/dbot/removeCompanyBoardFromArticlesOfAssociation/removeCompanyBoardFromArticlesOfAssociationHandler";
import {
  RemoveSignatureFromArticlesOfAssociation,
  RemoveSignatureFromArticlesOfAssociationForm,
} from "@/dbot/removeSignatureFromArticlesOfAssociation/removeSignatureFromArticlesOfAssociationDomain";
import { removeSignatureFromArticlesOfAssociationHandler } from "@/dbot/removeSignatureFromArticlesOfAssociation/removeSignatureFromArticlesOfAssociationHandler";
import ReverseSplitShareForm, {
  ReverseSplitShare,
} from "@/dbot/reverseSplitShare/reverseSplitShareDomain";
import { reverseSplitHandler } from "@/dbot/reverseSplitShare/reverseSplitShareHandler";
import { showSaveProjectAsTemplateDialog } from "@/templates/saveTemplateDomain";
import { canSaveTemplates } from "@/templates/templateDomain";
import { SplitShare, SplitShareForm } from "@/dbot/splitShare/splitShareDomain";
import { splitShareHandler } from "@/dbot/splitShare/splitShareHandler";
import {
  SubscriptionForm,
  SubscriptionFormForm,
} from "@/dbot/subscriptionForm/subscriptionFormDomain";
import { subscriptionFormHandler } from "@/dbot/subscriptionForm/subscriptionFormHandler";
import { SummaryController } from "@/dbot/summary/SummaryController";
import {
  DecisionListItem,
  getDecisionFollowUpPanels,
  openSigningStatusDialogAction,
} from "@/dbot/summary/summaryDomain";
import { DocumentApiClient, DocumentSigningStatus } from "@/document/documentApi";
import {
  confirmAction,
  DCEvent,
  HttpResponse,
  showError,
  showGeneralError,
  showGeneralSuccess,
  showPaymentDialog,
  showSuccess,
  showWarning,
  thunk,
} from "@/events/eventsDomain";
import { ConversionModel } from "@/fileUpload/ConversionModel";
import {
  FileUploadConfig,
  toPdfOrOfficeUploadConfig,
  ProjectDecisionAttachment,
  UploadedAttachment,
} from "@/fileUpload/fileUploadDomain";
import { Language } from "@/i18n/languages";
import { TFunction, TMessage } from "@/i18n/tt";
import AdminElectronicGeneralMeetingInitializer from "@/meetings/generalMeeting/AdminElectronicGeneralMeetingPage";
import { AdminElectronicGeneralMeetingController } from "@/meetings/generalMeeting/electronicGeneralMeetingDomain";
import { MeetingType } from "@/meetings/meetingsDomain";
import ReviewMinutesAdminInitializer from "@/meetings/ReviewMinutesPageAdmin";
import { MeetingEmailInitializerController } from "@/noticeForBoardMember/noticeForBoardMemberDomain";
import { EnterpriseAccountsForUserResponse, requiresSubscription } from "@/payment/Payment";
import { ListAdminAccessResponse } from "@/types/Settings";
import {
  TransactionErrorResponse,
  transactionValidationErrorMessage,
} from "@/stores/TransactionStore";
import { CompanyRole, SuggestedCompany } from "@/types/CompanyInfo";
import { AccessType } from "@/types/enums/AccessType";
import { ObjectPermission } from "@/types/Permissions";
import {
  ShareholderField,
  updateShareholderEmailBatchRequest,
  UpdateShareholderEmailBatchResponse,
} from "@/types/Shareholder";
import { User } from "@/types/UserInfo";
import { move, next, prev } from "@/utils/arrays";
import { isProductionEnvironment } from "@/utils/environments";
import { deferred, when } from "@/utils/functions";
import {
  isArrowDown,
  isArrowLeft,
  isArrowRight,
  isArrowUp,
  isTargetInput,
} from "@/utils/keyboardEvents";
import { logDebug } from "@/utils/log";
import { navigateToProjectList, openLinkInNewWindow } from "@/utils/navigation";
import { toQueryString } from "@/utils/queryParams";
import { toAttachmentId, toProjectReference } from "@/utils/UrnReference";
import { AxiosInstance, AxiosResponse } from "axios";
import { History } from "history";
import { compact, get, identity, isArray, isEmpty, mapValues, noop, sortBy, uniq } from "lodash";
import { action, comparer, makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import { ReactElement } from "react";
import { projectApi, ProjectStatusResponse } from "../common/projectApi";
import { Incorporation, IncorporationForm } from "../incorporation/incorporationDomain";
import { incorporationHandler } from "../incorporation/incorporationHandler";
import { ConflictMessage } from "./projectComponents";

///
/// Navigation state
///

type ProjectViewState = {
  activeTab?: TabId;
  detailsPanel?: DetailsPanelRef;
  detailsPanelMaximized?: boolean;
  displayMode?: "normal" | "fullscreen" | "screen-share" | "overlay";
  isMobileDocumentPreviewOpen?: boolean;
  selectedDocumentId?: string;
  selectedDocumentPreview?: string;
  selectedDocumentPreviewBaseVersion?: boolean;
};

export type ProjectNavigationState = {
  type: "project-navigation-state";
  from?: "create-new" | string;
  overlay?: boolean;
  documentId?: string;
  view?: ProjectViewState;
};

export const isProjectNavigationState = (x: unknown): x is ProjectNavigationState =>
  get(x, "type") === "project-navigation-state";

export const asProjectNavigationState = (x: unknown): ProjectNavigationState | undefined =>
  isProjectNavigationState(x) ? x : undefined;

const toProjectNavigationState = (
  from?: string,
  view?: ProjectViewState,
): ProjectNavigationState => ({ type: "project-navigation-state", from, view });

//
// Decision
//

export type DecisionData =
  | ApproveAnnualAccount
  | ApproveAuditorRemuneration
  | ApproveCompanyBoardRemuneration
  | ApproveNominationCommitteeRemuneration
  | AssignsNewCompanyBoard
  | BoardAssignsSignature
  | CapitalIncreaseThroughBonusIssue
  | ChangeBusinessPurpose
  | ChangeOfAddress
  | ChangeOfBusinessManager
  | ChangeCompanyBoardInArticlesOfAssociation
  | ChangeOfAuditor
  | ChangeOfAccountant
  | ChangeSignatureInArticlesOfAssociation
  | CompanyNameChange
  | ConvertibleLoanPowerOfAttorney
  | Dividend
  | DividendPowerOfAttorney
  | ElectNominationCommittee
  | EmploymentOfManagingDirector
  | Freetext
  | GroupContributionByGeneralMeeting
  | IncreaseOfShareCapital
  | IncreaseShareCapitalPowerOfAttorney
  | InsertCompanyBoardInArticlesOfAssociation
  | InsertSignatureInArticlesOfAssociation
  | InterimBalanceSheet
  | DissolveCompany
  | LiquidateCompany
  | NoAuditor
  | NoAccountant
  | NoBusinessManager
  | Procura
  | ReductionOfShareCapital
  | RemoveCompanyBoardFromArticlesOfAssociation
  | NoManagingDirector
  | ShareTransferConsent
  | OptionGrantLetter
  | OptionExercise
  | RemoveSignatureFromArticlesOfAssociation
  | ReverseSplitShare
  | SplitShare
  | ChangeArticlesOfAssociation
  | ConversionOfShares
  | AcquireOwnSharesPowerOfAttorney
  | ElectionOfCompanyBoard
  | Incorporation
  | Merger;

export type DecisionForm =
  | ApproveAnnualAccountForm
  | ApproveAuditorRemunerationForm
  | ApproveCompanyBoardRemunerationForm
  | ApproveNominationCommitteeRemunerationForm
  | AssignsNewCompanyBoardForm
  | BoardAssignsSignatureForm
  | CapitalIncreaseThroughBonusIssueForm
  | ChangeBusinessPurposeForm
  | ChangeOfAddressForm
  | ChangeOfBusinessManagerForm
  | ChangeCompanyBoardInArticlesOfAssociationForm
  | ChangeOfAuditorForm
  | ChangeOfAccountantForm
  | ChangeSignatureInArticlesOfAssociationForm
  | CompanyNameChangeForm
  | ConvertibleLoanPowerOfAttorneyForm
  | DividendForm
  | DividendPowerOfAttorneyForm
  | ElectNominationCommitteeForm
  | EmploymentOfManagingDirectorForm
  | FreetextForm
  | GroupContributionByGeneralMeetingForm
  | IncreaseOfShareCapitalForm
  | IncreaseShareCapitalPowerOfAttorneyForm
  | InsertCompanyBoardInArticlesOfAssociationForm
  | InsertSignatureInArticlesOfAssociationForm
  | InterimBalanceSheetForm
  | DissolveCompanyForm
  | LiquidateCompanyForm
  | NoAuditorForm
  | NoAccountantForm
  | NoBusinessManagerForm
  | ProcuraForm
  | ReductionOfShareCapitalForm
  | RemoveCompanyBoardFromArticlesOfAssociationForm
  | NoManagingDirectorForm
  | ShareTransferConsentForm
  | OptionGrantLetterForm
  | OptionExerciseForm
  | RemoveSignatureFromArticlesOfAssociationForm
  | ReverseSplitShareForm
  | SplitShareForm
  | ChangeArticlesOfAssociationForm
  | ConversionOfSharesForm
  | AcquireOwnSharesPowerOfAttorneyForm
  | ElectionOfCompanyBoardForm
  | IncorporationForm
  | MergerForm;

export type Decision = {
  decisionId: string;
  decisionData: DecisionData;
  decisionTexts: LocalizedTextForDocument[];
  decisionIntroTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
};

export type DecisionAndForm<T extends DecisionType> = {
  type: T;
  decisionId: string;
  decision: Decision;
  form: DecisionForm;
  decisionTexts: LocalizedTextForDocument[];
  decisionIntroTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
};

export const handlers = {
  [DecisionType.approveAnnualAccount]: approveAnnualAccountHandler,
  [DecisionType.approveAuditorRemuneration]: approveAuditorRemunerationHandler,
  [DecisionType.approveCompanyBoardRemuneration]: approveCompanyBoardRemunerationHandler,
  [DecisionType.approveNominationCommitteeRemuneration]:
    approveNominationCommitteeRemunerationHandler,
  [DecisionType.assignsNewCompanyBoard]: assignsNewCompanyBoardHandler,
  [DecisionType.auditor]: auditorHandler,
  [DecisionType.accountant]: accountantHandler,
  [DecisionType.boardAssignsSignature]: boardAssignsSignatureHandler,
  [DecisionType.capitalIncreaseThroughBonusIssue]: capitalIncreaseThroughBonusIssueHandler,
  [DecisionType.changeBusinessPurpose]: changeBusinessPurposeHandler,
  [DecisionType.changeOfAddress]: changeOfAddressHandler,
  [DecisionType.changeOfBusinessManager]: changeOfBusinessManagerHandler,
  [DecisionType.changeCompanyBoardInArticlesOfAssociation]:
    changeCompanyBoardInArticlesOfAssociationHandler,
  [DecisionType.changeSignatureInArticlesOfAssociation]:
    changeSignatureInArticlesOfAssociationHandler,
  [DecisionType.companyName]: companyNameHandler,
  [DecisionType.convertibleLoanPowerOfAttorney]: convertibleLoanPoAHandler,
  [DecisionType.dividend]: dividendHandler,
  [DecisionType.dividendPowerOfAttorney]: dividendPowerOfAttorneyHandler,
  [DecisionType.electNominationCommittee]: electNominationCommitteeHandler,
  [DecisionType.employmentOfManagingDirector]: employmentOfManagingDirectorHandler,
  [DecisionType.freetext]: freetextHandler,
  [DecisionType.groupContributionByGeneralMeeting]: groupContributionByGeneralMeetingHandler,
  [DecisionType.increaseOfShareCapital]: increaseOfShareCapitalHandler,
  [DecisionType.increaseShareCapitalPowerOfAttorney]: increaseShareCapitalPowerOfAttorneyHandler,
  [DecisionType.insertCompanyBoardInArticlesOfAssociation]:
    insertCompanyBoardInArticlesOfAssociationHandler,
  [DecisionType.insertSignatureInArticlesOfAssociation]:
    insertSignatureInArticlesOfAssociationHandler,
  [DecisionType.interimBalanceSheet]: interimBalanceSheetHandler,
  [DecisionType.dissolveCompany]: dissolveCompanyHandler,
  [DecisionType.liquidateCompany]: liquidateCompanyHandler,
  [DecisionType.noAuditor]: noAuditorHandler,
  [DecisionType.noAccountant]: noAccountantHandler,
  [DecisionType.noBusinessManager]: noBusinessManagerHandler,
  [DecisionType.procura]: procuraHandler,
  [DecisionType.noManagingDirector]: noManagingDirectorHandler,
  [DecisionType.shareTransferConsent]: shareTransferConsentHandler,
  [DecisionType.optionGrantLetter]: optionGrantLetterHandler,
  [DecisionType.optionExercise]: optionExerciseHandler,
  [DecisionType.reductionOfShareCapital]: reductionOfShareCapitalHandler,
  [DecisionType.removeCompanyBoardFromArticlesOfAssociation]:
    removeCompanyBoardFromArticlesOfAssociationHandler,
  [DecisionType.removeSignatureFromArticlesOfAssociation]:
    removeSignatureFromArticlesOfAssociationHandler,
  [DecisionType.reverseSplitShare]: reverseSplitHandler,
  [DecisionType.splitShare]: splitShareHandler,
  [DecisionType.changeArticlesOfAssociation]: changeArticlesOfAssociationHandler,
  [DecisionType.conversionOfShares]: conversionOfSharesHandler,
  [DecisionType.acquireOwnSharesPowerOfAttorney]: acquireOwnSharesPowerOfAttorneyHandler,
  [DecisionType.electionOfCompanyBoard]: electionOfCompanyBoardHandler,
  [DecisionType.incorporation]: incorporationHandler,
  [DecisionType.merger]: mergerHandler,
};

export function getHandler(
  type: DecisionType,
): DecisionTypeHandler<DecisionType, DecisionData, DecisionForm> {
  if (!(type in handlers)) {
    throw new Error(`Missing handler for type: ${type}`);
  }
  return handlers[type] as DecisionTypeHandler<DecisionType, DecisionData, DecisionForm>;
}

export function applyForm<T extends DecisionType>({
  type,
  decisionId,
  form,
}: DecisionAndForm<T>): DecisionChanged {
  return {
    type: "decision-changed",
    decisionId,
    decisionData: getHandler(type).applyForm(form),
  };
}

export function applyDecisionAttachments({
  decisionId,
  attachments,
}: {
  decisionId: string;
  attachments: ProjectDecisionAttachment[];
}): DecisionAttachmentsChanged {
  return {
    type: "decision-attachments-changed",
    decisionId,
    attachments: attachments.map((a) => ({
      id: a.id,
      filename: a.filename,
      selectedDocuments: a.selectedDocuments.slice(),
      originalFileType: a.originalFileType,
    })),
  };
}

export function removeDecision(
  decisions: DecisionAndForm<DecisionType>[],
  type: DecisionType,
): DecisionAndForm<DecisionType> | undefined {
  const index = decisions.findIndex((x) => x.type === type);
  if (index !== -1) {
    return decisions.splice(index, 1)[0];
  }
}

export function removeDecisionById(
  decisions: DecisionAndForm<DecisionType>[],
  id: string,
): DecisionAndForm<DecisionType> | undefined {
  const index = decisions.findIndex((x) => x.decisionId === id);
  if (index !== -1) {
    return decisions.splice(index, 1)[0];
  }
}

//
// Document
//

export type DocumentData =
  | ArticlesOfAssociation
  | NoticeOfBoardMeeting
  | BoardMeetingMinutes
  | NoticeToGeneralMeeting
  | GeneralMeetingMinutes
  | SubscriptionForm
  | CustomDocumentData;

export type DocumentOverrides = CustomDocumentOverrides;

export type DocumentForm =
  | NoticeOfBoardMeetingForm
  | NoticeToGeneralMeetingForm
  | BoardMeetingMinutesForm
  | GeneralMeetingMinutesForm
  | SubscriptionFormForm
  | ArticlesOfAssociationForm
  | CustomDocumentForm;

export type Document = {
  documentId: string;
  documentData: DocumentData;
  attachments?: UploadedAttachment[];
};

export type DocumentAndForm = {
  documentId: string;
  type: DocumentType;
  form: DocumentForm;
  attachments: UploadedAttachment[];
  autoAttachments?: AttachmentPreview[];
};

const documentHandlers = {
  [DocumentType.notice_board_meeting]: noticeBoardMeetingHandler,
  [DocumentType.notice_general_meeting]: noticeGeneralMeetingHandler,
  [DocumentType.board_meeting]: boardMeetingMinutesHandler,
  [DocumentType.general_meeting]: generalMeetingMinutesHandler,
  [DocumentType.subscription_form]: subscriptionFormHandler,
  [DocumentType.articlesofassociation]: articlesOfAssociationHandler,
  [DocumentType.custom_document]: customDocumentHandler,
};

export function getDocumentHandler(
  type: DocumentType,
): DocumentTypeHandler<DocumentType, DocumentData, DocumentForm> {
  if (!(type in documentHandlers)) {
    throw new Error(`Missing handler for type: ${type}`);
  }
  return documentHandlers[type] as DocumentTypeHandler<DocumentType, DocumentData, DocumentForm>;
}

export function applyDocumentForm({ documentId, type, form }: DocumentAndForm): Document {
  return {
    documentId: documentId,
    documentData: getDocumentHandler(type).applyForm(form),
  };
}

export const movedById = <T extends { id: string }>(items: T[], id: string, toId: string): T[] => {
  const item = items.find((i) => i.id === id);
  const toIndex = items.findIndex((i) => i.id === toId);
  return item && toIndex >= 0 ? move(items, item, toIndex) : items;
};

function applyDocumentAttachments({
  documentId,
  attachments,
}: DocumentAndForm): DocumentAttachmentsChanged {
  return {
    type: "document-attachments-changed",
    documentId: documentId,
    attachments: attachments.slice(),
  };
}

export function applyDocumentOverrides(
  { type, documentId, form }: DocumentAndForm,
  orgNos: string[],
): BatchDocumentOverrrides[] {
  return compact(
    orgNos.map((orgNo) => {
      const overrides = getDocumentHandler(type).applyOverrides?.(form, orgNo);
      return overrides ? { documentId, orgNo, overrides } : undefined;
    }),
  );
}

export function removeDocument(
  documents: DocumentAndForm[],
  documentId: string,
): DocumentAndForm | undefined {
  const index = documents.findIndex((x) => x.documentId === documentId);
  if (index !== -1) {
    return documents.splice(index, 1)[0];
  }
}

export function getDocumentLanguages(documents: Document[], { orgNo }: { orgNo: string }) {
  const languages = uniq(documents.flatMap((d) => d.documentData.documentLanguage ?? []));
  return languages.length > 0 ? languages : defaultDocumentLanguage(orgNo);
}

export function findNoticeToBoardMeetingForm(
  documents: DocumentAndForm[],
): NoticeOfBoardMeetingForm | undefined {
  return documents
    .map((d) => d.form)
    .flatMap((f) => (f.type === DocumentType.notice_board_meeting ? [f] : []))[0];
}

export function findNoticeToGeneralMeetingForm(
  documents: DocumentAndForm[],
): NoticeToGeneralMeetingForm | undefined {
  return documents.map((d) => d.form).find((f) => f.type === DocumentType.notice_general_meeting);
}

export function findDateNoticeGeneralMeeting(documents: DocumentAndForm[]): string | undefined {
  return findNoticeToGeneralMeetingForm(documents)?.dateNoticeGeneralMeeting?.validatedValue;
}

///
/// Project controller
///

class ContextWrapper implements ProjectContext {
  constructor(
    private projectController: ProjectController,
    private projectParams: ProjectInitializationParams,
  ) {
    makeAutoObservable(this);
  }

  get userPrefs() {
    return this.projectParams.rootStoreData.userPrefs ?? {};
  }

  get currentDecisions(): DecisionItem[] {
    return this.projectController.decisions.map((d) =>
      toDecisionItem(
        d,
        this.projectController.invalidDecisions.has(d.decisionId),
        this.projectController.project.decisionsRequiresGeneralMeeting.includes(d.decisionId),
        this.projectController.project.defaultDecisionsGeneralMeetingVotingType[d.decisionId],
        this.projectController.project.decisionsApprovedByGeneralMeeting[d.decisionId],
      ),
    );
  }

  get currentDocuments(): DocumentItem[] {
    return this.projectController.sortedDocuments.map((df) => {
      const d = this.projectController.project.jsonData.documents.find(
        (x) => x.documentId === df.documentId,
      )!;
      return {
        documentId: d.documentId,
        type: d.documentData.type,
        title:
          getDocumentHandler(df.form.type).getTitle?.(df.form, this.locale) ??
          getDocumentLabel(d.documentData.type),
        documentData: d.documentData,
        languages: d.documentData.documentLanguage,
        hasValidationError: this.projectController.invalidDecisions.has(d.documentId),
      };
    });
  }

  get documentLanguages(): Language[] {
    return getDocumentLanguages(this.projectController.project.jsonData.documents, this);
  }

  get documentStatuses(): ProjectDocumentStatus[] {
    return this.projectController.documentStatuses;
  }

  get isLocked(): boolean {
    return this.projectController.isLocked;
  }

  get dissolutionStatus(): DissolutionStatus | undefined {
    return this.projectController.project.dissolutionStatus;
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.projectParams.fileUploadConfig;
  }

  get boardMeetingFields(): BoardMeetingFields {
    return this.projectController.boardMeetingFields;
  }

  get generalMeetingFields(): GeneralMeetingFields {
    return this.projectController.generalMeetingFields;
  }

  get orgNo(): string {
    return this.projectController.project.orgNo;
  }

  get projectId(): string {
    return this.projectController.projectId;
  }

  get user(): User {
    return this.projectController.initializationParams.user;
  }

  get boardMeetingNoticeRecipients(): Recipient[] {
    const form = findNoticeToBoardMeetingForm(this.projectController.documents);
    return form ? toRecipients(this.initialState.roles, form) : [];
  }

  get signingBoardMembers(): CompanyRoleIdWithName[] {
    return this.projectController.documents
      .map((d) => d.form)
      .flatMap((f) =>
        f.type === DocumentType.board_meeting ? f.selectedSigningBoardMembers.selected : [],
      );
  }

  get currentSubscription(): PaidSubscription | undefined {
    return this.projectParams.currentSubscription;
  }

  get subscriptionTypeModel(): SubscriptionTypeModel {
    return {
      currentSubscription: this.currentSubscription,
      requiredSubscriptionType: getRequiredSubscriptionType(
        this.currentDecisions.map((d) => d.type),
      ),
    };
  }

  get contextData() {
    return this.projectController.project.contextData;
  }

  get initialState(): Evaluation {
    return this.contextData[this.orgNo].initialState;
  }

  get inputStates(): InputState[] {
    return this.contextData[this.orgNo]?.inputStates ?? [];
  }

  get aiClient(): AIClient {
    return this.projectParams.aiClient;
  }

  get projectStatus(): ProjectStatus {
    return this.projectController.project.projectStatus;
  }

  get isSharepointEnabled(): boolean {
    return this.projectController.project.isSharepointEnabled;
  }

  get locale() {
    return this.projectController.locale;
  }

  get detailsPanelMaximized() {
    return this.projectController.detailsPanelMaximized;
  }

  get boardMeetingVotes(): BoardMeetingVote[] {
    return this.projectController.project.boardMeetingVotes;
  }

  get validationErrors() {
    return this.projectController.validationErrors;
  }

  get displayMode() {
    return this.projectController.displayMode;
  }

  get participationList(): GeneralMeetingParticipation[] {
    return this.projectController.project.participationList;
  }

  get powerOfAttorneys(): PowerOfAttorneyForGeneralMeeting[] {
    return this.projectController.project.powerOfAttorneys;
  }

  get availableCompanies() {
    return sortedCompanies(
      this.projectParams.companies.map((c) => ({ ...c, isMainCompany: c.orgNo === this.orgNo })),
    );
  }

  get companies() {
    return this.availableCompanies.filter((c) =>
      this.projectController.project.companies.includes(c.orgNo),
    );
  }

  get isBatchProject() {
    return this.projectController.project.companies.length > 1;
  }

  get documentSigners() {
    return this.projectController.documentSigners;
  }

  get state() {
    return this.projectController.state;
  }

  get jsonData() {
    return this.projectController.project.jsonData;
  }

  get isProd() {
    return isProductionEnvironment();
  }

  get featureToggles() {
    return this.projectParams.featureToggles;
  }

  get subscribers() {
    return this.projectController.project.subscribers;
  }

  getUploadingDocumentBlob(
    documentId: string,
    orgNo: string,
  ): { filename: string; progress: number; isConverting: boolean } | undefined {
    const entry = this.projectController.customDocumentUpload.find(
      (x) => x.documentId === documentId && x.orgNo === orgNo,
    );
    if (!entry) return undefined;
    const progressKey = `${documentId}:${orgNo}`;
    const progress = this.projectController.customDocumentProgress.get(progressKey) ?? 0;
    const isConverting = this.projectController.customDocumentConverting.some(
      (x) =>
        x.documentId === documentId && x.orgNo === orgNo && isPending(x.model.conversionResult),
    );
    return { filename: entry.files[0]?.name ?? "", progress, isConverting };
  }
}

export function getRequiredSubscriptionType(
  currentDecisions: DecisionType[],
): SubscriptionType | undefined {
  const uniqueSubscriptionTypes = new Set(
    currentDecisions.map((it) => {
      return categorySubscriptionMapping[categoryMapping[it]];
    }),
  );
  if (uniqueSubscriptionTypes.has(SubscriptionType.PRO)) {
    return SubscriptionType.PRO;
  } else if (uniqueSubscriptionTypes.has(SubscriptionType.BASIC)) {
    return SubscriptionType.BASIC;
  }
  return undefined;
}

export function updateDetailsPanelDocumentContext(
  documentId: string,
  detailsPanel: DetailsPanelRef | undefined,
  documents: DocumentItem[],
): DetailsPanelRef | undefined {
  const document = documents.find((d) => d.documentId === documentId);
  switch (document?.type) {
    case DocumentType.notice_general_meeting:
    case DocumentType.general_meeting:
    case DocumentType.notice_board_meeting:
    case DocumentType.board_meeting:
      switch (detailsPanel?.type) {
        case "decision":
          return {
            ...detailsPanel,
            documentId: documentId,
          };
      }
  }
}

type TabType = DocumentType | "review-matters" | "conduct-general-meeting";
type CustomTab = {
  type: "review-matters" | "conduct-general-meeting";
  content: { type: "custom"; content: ReactElement; attachments?: AttachmentPreview[] };
  documentId: string;
  scrollToId?: string;
  title: TMessage;
  recipientLabel?: TMessage;
  nextRecipient?: () => void;
  prevRecipient?: () => void;
  recipientSelector?: SelectProps<RecipientId>;
  companyLabel?: TMessage;
  nextCompany?: () => void;
  prevCompany?: () => void;
  companySelector?: SelectProps<string>;
  download?: () => DownloadAsPdfButtonProps | undefined;
  upload?: (
    params: { documentId: string; orgNo?: string },
    files: File[],
    fileUploadConfig: FileUploadConfig,
  ) => void;
  uploadFileConfig?: FileUploadConfig;
  isUploading?: (params: { documentId: string; orgNo?: string }) => boolean;
  isConverting?: (params: { documentId: string; orgNo?: string }) => boolean;
  extraAction?: DocumentPreviewProps["extraAction"];
};

type DocumentTab = {
  type: DocumentType;
  content?: {
    type: "document";
    documentId: string;
    orgNo?: string;
    content?: string;
    attachments?: AttachmentPreview[];
  }[];
  documentId: string;
  scrollToId?: string;
  uploadedAttachments?: UploadedAttachment[];
  uploadingAttachments?: UploadingAttachment[];
  title: TMessage;
  recipientLabel?: TMessage;
  nextRecipient?: () => void;
  prevRecipient?: () => void;
  recipientSelector?: SelectProps<RecipientId>;
  companyLabel?: TMessage;
  nextCompany?: () => void;
  prevCompany?: () => void;
  companySelector?: SelectProps<string>;
  download?: (params: {
    documentId: string;
    orgNo?: string;
  }) => DownloadAsPdfButtonProps | undefined;
  downloadAttachment?: (params: {
    documentId: string;
    orgNo?: string;
    type: AutoAttachmentType;
  }) => DownloadAsPdfButtonProps | undefined;
  upload?: (
    params: { documentId: string; orgNo?: string },
    files: File[],
    fileUploadConfig: FileUploadConfig,
  ) => void;
  uploadFileConfig?: FileUploadConfig;
  isUploading?: (params: { documentId: string; orgNo?: string }) => boolean;
  isConverting?: (params: { documentId: string; orgNo?: string }) => boolean;
  extraAction?: DocumentPreviewProps["extraAction"];
};

export type TabInPreview = CustomTab | DocumentTab;

export function isNonDocumentTab(tab?: TabType | string) {
  return (
    tab !== undefined &&
    (tab === "review-matters" ||
      tab === "conduct-general-meeting" ||
      tab === reviewDocumentId ||
      tab === conductGeneralMeetingDocumentId)
  );
}

export const shouldAddReviewTab = (
  documents: DocumentAndForm[],
  projectContext: ProjectContext,
): boolean => {
  const boardMinutes = documents.find((d) => d.type === DocumentType.board_meeting);
  if (boardMinutes !== undefined) {
    const minutesStatus = projectContext.documentStatuses.find(
      (d) => d.documentId === boardMinutes.documentId,
    )?.signingStatus;
    return (
      minutesStatus === DocumentSigningStatus.SENT_FOR_VOTING ||
      minutesStatus === DocumentSigningStatus.SENT_FOR_REVIEW
    );
  }
  return false;
};

export const shouldAddConductGeneralMeetingTab = (projectContext: ProjectContext): boolean => {
  return projectContext.generalMeetingFields.conductElectronicGeneralMeeting.value ?? false;
};

const getProjectName = (project: GetProjectResponse) =>
  project.name ?? project.generatedName ?? "???";

export type OpenProject = Pick<ProjectController, "projectId" | "name">;

type UpdateProjectRequest = { projectVersion: number; changes: ProjectChange[] };
export type UpdateProjectResponse = {
  project: GetProjectResponse;
  addedDecisionIds: string[];
  addedDocumentIds: string[];
  changedDocumentIds: string[];
  changedDecisionIds?: string[];
};

const decisionToOpen = <T extends { decisionId: string; type: DecisionType }>(
  decisions: T[],
): T | undefined =>
  decisions.length === 1 || decisions[0]?.type === DecisionType.optionExercise
    ? decisions[0]
    : undefined;

type ProjectChange =
  | BoardMeetingChanged
  | CompaniesChanged
  | DecisionAdded
  | DecisionAttachmentMoved
  | DecisionAttachmentsChanged
  | DecisionChanged
  | DecisionMoved
  | DecisionRemoved
  | DecisionTextsChanged
  | DecisionIntroTextsChanged
  | DecisionsCopied
  | DocumentAdded
  | DocumentAttachmentMoved
  | DocumentAttachmentsChanged
  | DocumentChanged
  | DocumentOverridesChanged
  | DocumentRemoved
  | GeneralMeetingChanged
  | ProjectNameChanged;

type CompaniesChanged = { type: "companies-changed"; companies: string[] };

type DecisionAdded = {
  type: "decision-added";
  decisionType: DecisionType;
  title?: string;
  freetextType?: CustomizedFreetextType;
  index?: number;
  documentId?: string;
};

type DecisionAttachmentMoved = {
  type: "decision-attachment-moved";
  decisionId: string;
  attachmentId: string;
  toAttachmentId: string;
};
type DecisionAttachmentsChanged = {
  type: "decision-attachments-changed";
  decisionId: string;
  attachments: ProjectDecisionAttachment[];
};
type DecisionChanged = { type: "decision-changed"; decisionId: string; decisionData: DecisionData };
type DecisionMoved = { type: "decision-moved"; decisionId: string; toDecisionId: string };
type DecisionRemoved = { type: "decision-removed"; decisionId: string };
type DecisionTextsChanged = {
  type: "decision-texts-changed";
  decisionId: string;
  texts: LocalizedTextForDocument[];
};
type DecisionIntroTextsChanged = {
  type: "decision-intro-texts-changed";
  decisionId: string;
  texts: LocalizedTextForDocument[];
};
type DecisionsCopied = {
  type: "decisions-copied";
  sourceProjectId: string;
  sourceDecisionIds: string[];
  documentId?: string;
};
type DocumentAdded = {
  type: "document-added";
  documentType: DocumentType;
  customDocumentType?: CustomDocumentType;
  customDocumentTitle?: string;
  customDocumentInitialRecipients?: CustomDocumentInitialRecipients;
};
type DocumentAttachmentMoved = {
  type: "document-attachment-moved";
  documentId: string;
  attachmentId: string;
  toAttachmentId: string;
};
type DocumentAttachmentsChanged = {
  type: "document-attachments-changed";
  documentId: string;
  attachments: UploadedAttachment[];
};
type DocumentChanged = { type: "document-changed"; documentId: string; documentData: DocumentData };
type DocumentOverridesChanged = {
  type: "document-overrides-changed";
  documentId: string;
  orgNo: string;
  overrides?: DocumentOverrides;
};
type DocumentRemoved = { type: "document-removed"; documentId: string };
type BoardMeetingChanged = { type: "board-meeting-changed"; boardMeeting?: BoardMeeting };
type GeneralMeetingChanged = { type: "general-meeting-changed"; generalMeeting?: GeneralMeeting };
type ProjectNameChanged = { type: "project-name-changed"; name: string };

const INCORPORATION_STATUS_POLL_INTERVAL_MS = 5 * 60 * 1_000;

export function incorporationDecisionsPendingBrReview(
  followUps: GetProjectResponse["followUps"],
): string[] {
  return followUps
    .filter((decisionFollowUp) => {
      const sendToBr = decisionFollowUp.followUps
        .map((followUp) => followUp.data)
        .filter((data): data is SendToBRFollowUp => data.type === FollowUpType.sendToBR);
      return (
        sendToBr.some((data) => data.brStatus === "SENT_TO_BR") &&
        sendToBr.some((data) => parseDseSubmitAck(data.brResultData) !== undefined)
      );
    })
    .map((decisionFollowUp) => decisionFollowUp.decisionId);
}

export class ProjectController {
  http: AxiosInstance;
  documentApiClient: DocumentApiClient;
  initializationParams: ProjectInitializationParams;
  locale = Language.EN;
  decisions: DecisionAndForm<DecisionType>[] = [];
  reactionDisposers: Map<string, CleanupFunction> = new Map<string, CleanupFunction>();
  documents: DocumentAndForm[] = [];
  project: GetProjectResponse;
  boardMeetingFields: BoardMeetingFields;
  generalMeetingFields: GeneralMeetingFields;
  documentSigners: Map<string, Result<GetProjectDocumentSignersResponse>> = new Map<
    string,
    Result<GetProjectDocumentSignersResponse>
  >();
  documentRecipientPreviews: {
    [key: string]: Result<DocumentPreview> | undefined;
  } = {};
  incorporationCompanyPreviews: {
    [key: string]: Result<DocumentPreview> | undefined;
  } = {};
  selectedRecipientId?: RecipientId;
  selectedCompany?: string;
  scrollToId?: string;

  state: ProjectControllerState = readyState();
  saveQueue: ProjectChange[] = [];
  currentSaveQueueItems: ProjectChange[] = [];
  onSaveDone: (
    | DbotEvent
    | DCEvent
    | ((err: unknown, result?: UpdateProjectResponse) => (DbotEvent | DCEvent)[])
  )[] = [];
  invalidDecisions: Set<string> = new Set();
  invalidDocuments: Set<string> = new Set();
  pendingSendForSigningPrekontroll?: {
    decisionId: string;
    contextDocumentId: string;
    onSuccess: DbotEvent;
  };
  prekontrollFailure?: {
    decisionId: string;
    message: TMessage;
    isSystemUnavailable: boolean;
  };
  projectContext: ProjectContext;
  summaryController: SummaryController;

  from?: string;
  view: ProjectViewState = {};

  eventListeners: Array<(event: DbotEvent) => void> = [];

  readonly history: History;
  readonly orgNo: string;
  readonly accessType: AccessType;
  reviewController: MeetingEmailInitializerController;
  electronicGMController: AdminElectronicGeneralMeetingController;
  isDownloadingAttachment = false;
  validationErrors?: TMessage[];

  customDocumentUpload: { documentId: string; orgNo?: string; files: File[] }[] = [];
  customDocumentConverting: { documentId: string; orgNo?: string; model: ConversionModel }[] = [];
  customDocumentProgress: Map<string, number> = new Map();

  editedDecision?: {
    orgNo: string;
    decisionId: string;
    decisionType: DecisionType;
    decision: DecisionAndForm<DecisionType>;
  };

  nameGenerator?: (jsonData: ProjectJsonData) => string;
  replaceState: () => void = noop;

  constructor(
    project: GetProjectResponse,
    params: ProjectInitializationParams,
    http: AxiosInstance,
    history: History,
    accessType: AccessType,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.history = history;
    this.accessType = accessType;
    this.http = http;
    this.orgNo = project.orgNo;
    this.documentApiClient = new DocumentApiClient(http, this.orgNo);
    this.initializationParams = params;
    this.project = project;
    this.projectContext = new ContextWrapper(this, params);
    this.boardMeetingFields = createBoardMeetingFields({
      onChange: () => {
        logDebug("ProjectController: boardMeetingFields has changed");
        this.boardMeetingFields.isChanged = true;
        this.save([
          {
            type: "board-meeting-changed",
            boardMeeting: prepareBoardMeetingForSave(
              this.boardMeetingFields,
              this.project.jsonData,
            ),
          },
        ]);
      },
      jsonData: project.jsonData,
      aiClient: params.aiClient,
      isLocked: project.isLocked,
      defaultDocumentLanguage: defaultDocumentLanguage(project.orgNo),
    });
    this.generalMeetingFields = this.initGeneralMeetingFields();
    project.jsonData.decisions.forEach(this.initDecision);
    project.jsonData.documents.forEach((d) => this.setupDocument(d));
    this.summaryController = new SummaryController(
      this.dispatch,
      this.projectContext,
      project.followUps,
    );
    this.reviewController = new MeetingEmailInitializerController(params.rootDispatch, http, noop);
    this.electronicGMController = new AdminElectronicGeneralMeetingController(
      http,
      this.dispatch,
      this.projectContext,
      this.project.jsonData,
      () => this.project.electronicGeneralMeeting,
    );
  }

  init({
    navigationState,
    nameGenerator,
    replaceState,
  }: {
    navigationState?: ProjectNavigationState;
    nameGenerator?: (jsonData: ProjectJsonData) => string;
    replaceState?: (state: ProjectNavigationState) => void;
  } = {}): CleanupFunction {
    logDebug("ProjectController::init", navigationState);
    this.nameGenerator = nameGenerator;
    this.replaceState = () => replaceState?.(toProjectNavigationState(this.from, toJS(this.view)));

    // Note: we are deferring the initialization of the document forms until the ProjectController.init() function is called
    this.documents.forEach((d) => {
      this.initDocument(d);
    });

    const createdDecision = decisionToOpen(this.decisions);
    if (createdDecision && navigationState?.from === "create-new") {
      this.dispatch({
        type: "dbot/open-details-panel",
        panel: {
          type: "decision",
          decisionId: createdDecision.decisionId,
        },
      });
    }

    if (
      this.documents.length === 1 &&
      this.documents[0].type === DocumentType.custom_document &&
      navigationState?.from === "create-new" &&
      this.decisions.length === 0
    ) {
      this.dispatch(onCustomDocumentCreated({ addedDocumentIds: [this.documents[0].documentId] }));
    }

    if (navigationState?.from && navigationState?.from !== "create-new") {
      this.from = navigationState?.from;
    }

    if (navigationState?.overlay) {
      this.view.displayMode = "overlay";
    }

    if (navigationState?.view) {
      this.view = navigationState.view;
      this.initEditDecision();
    }

    if (navigationState?.documentId) {
      this.dispatch({
        type: "dbot/toggle-document-panel",
        documentId: navigationState?.documentId,
        expanded: true,
      });
    }

    this.initRecipientPreviewSync();
    this.initIncorporationPreviewSync();
    this.pollIncorporationStatus();
    this.replaceState();

    this.reactionDisposers.set(
      "save-queue",
      reaction(
        () => [this.currentSaveQueueItems.slice(), this.saveQueue.slice()],
        ([current, pending]) => {
          if (!current.length && pending.length) {
            runInAction(() => {
              this.currentSaveQueueItems = pending;
              this.saveQueue = [];
              this.doSave(pending, (err, result) => {
                const onSaveDone = this.onSaveDone.flatMap((x) =>
                  typeof x === "function" ? x(err, result) : [x],
                );
                this.currentSaveQueueItems = [];
                this.onSaveDone = [];
                this.dispatch(onSaveDone);
              });
            });
          }
        },
        { delay: 1 },
      ),
    );
    const conversionsInProgress = [
      ...this.project.jsonData.documents.flatMap((document) => document.attachments ?? []),
      ...this.project.jsonData.decisions.flatMap((decision) => decision.attachments ?? []),
    ].flatMap((attachment) => {
      if (!attachment?.documentConversionId) {
        return [];
      }
      return [
        {
          id: attachment.id,
          filename: attachment.filename,
          documentConversionId: attachment.documentConversionId,
        },
      ];
    });
    conversionsInProgress.forEach((conversion) => {
      this.dispatch({
        type: "dbot/update-uploading-attachment-props",
        reference: { type: "attachmentId", id: conversion.id },
        filename: conversion.filename,
        documentConversionId: conversion.documentConversionId,
        isConverting: true,
      });
      triggerConversionStatus(
        [{ id: conversion.id, filename: conversion.filename }],
        this.http,
        this.dispatch,
        conversion.documentConversionId,
      );
    });

    const previewShortcutHandler = (event: KeyboardEvent) => {
      if (!this.activeDocumentTab || !event.ctrlKey || isTargetInput(event)) {
        return;
      }

      switch (true) {
        case isArrowUp(event):
          this.activeDocumentTab.prevCompany?.();
          break;
        case isArrowDown(event):
          this.activeDocumentTab.nextCompany?.();
          break;
        case isArrowLeft(event):
          this.activeDocumentTab.prevRecipient?.();
          break;
        case isArrowRight(event):
          this.activeDocumentTab.nextRecipient?.();
          break;
      }
    };

    window.addEventListener("keydown", previewShortcutHandler);

    return () => {
      logDebug("ProjectController::cleanup", window.location.href);
      this.reactionDisposers.forEach((disposer) => {
        disposer();
      });
      this.electronicGMController.unsubscribeFromGeneralMeetingMessages();
      window.removeEventListener("keydown", previewShortcutHandler);
    };
  }

  setViewState(data: Partial<ProjectViewState>) {
    Object.assign(this.view, data);
    this.replaceState();
  }

  initGeneralMeetingFields(): GeneralMeetingFields {
    return createGeneralMeetingFields({
      roles: this.projectContext.initialState.roles,
      features: this.projectContext.featureToggles,
      jsonData: this.project.jsonData,
      isBatchProject: this.projectContext.isBatchProject,
      hasMultipleShareholders:
        shareholdersWithSharesExcludingSelf(
          this.projectContext.initialState.shareholders,
          this.projectContext.initialState.sharesForShareholders,
        ).length > 1,
      onChange: () =>
        this.save([
          {
            type: "general-meeting-changed",
            generalMeeting: prepareGeneralMeetingForSave(
              this.generalMeetingFields,
              this.project.jsonData,
            ),
          },
        ]),
    });
  }

  initDocument(d: DocumentAndForm) {
    this.reactionDisposers.set(
      d.documentId,
      reaction(
        () => applyDocumentForm(d),
        ({ documentId, documentData }, previous) => {
          logDebug(
            `ProjectController: document ${d.type} ${d.documentId} has changed`,
            documentData,
            previous.documentData,
          );
          this.save([{ type: "document-changed", documentId, documentData }]);
        },
      ),
    );
    this.reactionDisposers.set(
      `${d.documentId}/attachments`,
      reaction(
        () => applyDocumentAttachments(d),
        (change) => {
          logDebug(
            `ProjectController: document ${d.type} ${d.documentId} attachments have changed`,
          );
          this.save([change]);
        },
      ),
    );
    this.reactionDisposers.set(
      `${d.documentId}/overrides`,
      reaction(
        () =>
          this.projectContext.isBatchProject &&
          applyDocumentOverrides(d, Object.keys(this.project.companyData)),
        (overrides) => {
          if (overrides) {
            logDebug(`ProjectController: document ${d.type} ${d.documentId} overrides has changed`);
            this.save(overrides.map((o) => ({ ...o, type: "document-overrides-changed" })));
          }
        },
        { equals: comparer.structural },
      ),
    );
    this.reactionDisposers.set(
      `${d.documentId}/initForm`,
      getDocumentHandler(d.type).initForm(d.form) ?? noop,
    );
  }

  cleanupDocument(documentId: string) {
    this.reactionDisposers.get(documentId)?.();
    this.reactionDisposers.delete(documentId);
    this.reactionDisposers.get(`${documentId}/attachments`)?.();
    this.reactionDisposers.delete(`${documentId}/attachments`);
    this.reactionDisposers.get(`${documentId}/overrides`)?.();
    this.reactionDisposers.delete(`${documentId}/overrides`);
    this.reactionDisposers.get(`${documentId}/initForm`)?.();
    this.reactionDisposers.delete(`${documentId}/initForm`);
  }

  setLocale(locale: Language) {
    this.locale = locale;
  }

  get projectId(): string {
    return this.project.projectId;
  }

  get name(): string {
    return getProjectName(this.project);
  }

  get isReadonly(): boolean {
    return this.accessType !== AccessType.ADMIN;
  }

  get isLocked(): boolean {
    return this.project.isLocked;
  }

  get version(): number {
    return this.project.version;
  }

  get documentStatuses() {
    return this.project.documentStatuses;
  }

  get previews() {
    return this.project.previews;
  }

  get visibleTabs(): TabId[] {
    return getVisibleTabs({
      decisions: this.summaryController.decisionsForApply,
      decisionTypes: this.projectContext.currentDecisions.map((d) => d.type),
      documents: this.documents,
      locale: this.projectContext.locale,
      conductElectronicGeneralMeeting:
        this.projectContext.generalMeetingFields?.conductElectronicGeneralMeeting.value,
      displayMode: this.displayMode,
      isBatchProject: this.projectContext.isBatchProject,
      hasMergerWithCounterpart:
        findMergerWithCounterpart(this.projectContext.currentDecisions) !== undefined,
    });
  }

  get activeTab(): TabId {
    return this.view.activeTab ?? this.visibleTabs[0];
  }

  get detailsPanel(): DetailsPanelRef | undefined {
    return this.view.detailsPanel;
  }

  get detailsPanelMaximized(): boolean {
    return this.view.detailsPanelMaximized ?? false;
  }

  get displayMode(): "normal" | "fullscreen" | "screen-share" | "overlay" {
    return this.view.displayMode ?? "normal";
  }

  get isMobileDocumentPreviewOpen(): boolean {
    return this.view.isMobileDocumentPreviewOpen ?? false;
  }

  get documentIds(): string[] {
    return this.sortedDocuments.map((d) => d.documentId);
  }

  get selectedDocumentId(): string | undefined {
    if (this.view.selectedDocumentId && this.documentIds.includes(this.view.selectedDocumentId)) {
      return this.view.selectedDocumentId;
    }
    return this.documentIds[0];
  }

  get selectedDocumentPreview(): string | undefined {
    if (
      this.view.selectedDocumentPreview &&
      this.documentTabs.some((x) => x.documentId === this.view.selectedDocumentPreview)
    ) {
      return this.view.selectedDocumentPreview;
    }
    return this.sortedDocumentPreviews[0]?.documentId;
  }

  get isFullscreen() {
    return this.displayMode === "fullscreen";
  }

  get isScreenShareMode() {
    return this.displayMode === "screen-share";
  }

  get isOverlayMode() {
    return this.displayMode === "overlay";
  }

  get containsNoticeBoardMeeting() {
    return this.documents.some((d) => d.type === DocumentType.notice_board_meeting);
  }

  get containsNoticeGeneralMeeting() {
    return this.documents.some((d) => d.type === DocumentType.notice_general_meeting);
  }

  get containsAnyBoardMeetingDocuments() {
    return this.documents.some((d) =>
      [DocumentType.notice_board_meeting, DocumentType.board_meeting].includes(d.type),
    );
  }

  get containsAnyGeneralMeetingDocuments() {
    return this.documents.some((d) =>
      [DocumentType.notice_general_meeting, DocumentType.general_meeting].includes(d.type),
    );
  }

  get isDuplicateGeneralMeeting() {
    return this.containsAnyGeneralMeetingDocuments && !this.containsAnyBoardMeetingDocuments;
  }

  get isDuplicateBoardMeeting() {
    return !this.containsAnyGeneralMeetingDocuments && this.containsAnyBoardMeetingDocuments;
  }

  get close() {
    const state = asProjectNavigationState(this.history.location.state);
    return state?.from && state.from !== "create-new"
      ? () => this.dispatch([{ type: "navigate", url: state.from! }])
      : undefined;
  }

  dispatch(event: DbotEvent | DCEvent | (DbotEvent | DCEvent)[]) {
    if (Array.isArray(event)) {
      event.forEach(this.handleEvent);
    } else {
      this.handleEvent(event);
    }
  }

  get canSaveAsTemplate(): boolean {
    return canSaveTemplates(
      this.initializationParams.featureToggles,
      this.initializationParams.enterpriseAccountInfo,
    );
  }

  openSaveAsTemplateDialog() {
    this.dispatch([{ type: "dbot/open-save-as-template-dialog" }]);
  }

  setProject(project: GetProjectResponse, options?: { syncDocuments?: boolean }) {
    this.project = project;
    if (options?.syncDocuments) {
      this.syncDocumentsFromProject(project);
    }
    this.summaryController.followUps = project.followUps;
    this.dispatch(updateInputStateEvent());
    this.reviewController.reloadReview(findBoardMeetingMinutes(project.jsonData), this.orgNo);
    if (project.isLocked) {
      this.dispatch([showSuccess({ key: "dbot.projectLocked" })]);
    }
    this.electronicGMController.setProjectJsonData(project.jsonData);
    this.documentRecipientPreviews = {};
    this.incorporationCompanyPreviews = {};
    this.boardMeetingFields.isChanged = false;
    if (isAoAUpdateInProgress(project.aoaMetadata)) {
      this.pollProjectUpdates(project);
    }
  }

  syncDocumentsFromProject(project: GetProjectResponse) {
    const projectDocumentIds = new Set(project.jsonData.documents.map((d) => d.documentId));
    this.documents
      .filter((d) => !projectDocumentIds.has(d.documentId))
      .forEach((d) => {
        this.cleanupDocument(d.documentId);
        removeDocument(this.documents, d.documentId);
      });

    project.jsonData.documents.forEach((document) => {
      if (!this.documents.some((d) => d.documentId === document.documentId)) {
        this.initDocument(this.setupDocument(document));
      }
    });
  }

  updateViewingStatus(documentId: string, viewingStatuses: DocumentViewingStatus[]) {
    const documentStatus = this.documentStatuses.find((d) => d.documentId === documentId);
    if (documentStatus) {
      documentStatus.numberOfViewers = viewingStatuses.length;
      documentStatus.numberOfViewed = viewingStatuses.filter(
        (x) => x.viewedTime !== undefined,
      ).length;
    }
  }

  handleEvent(event: DbotEvent | DCEvent) {
    if (!isDbotEvent(event)) {
      this.initializationParams.rootDispatch([event]);
      return;
    }

    logDebug("ProjectController::dispatch", event);
    const dispatch = deferred(this.dispatch);
    switch (event.type) {
      case "dbot/reload-project":
        this.history.replace(this.history.location.pathname, null); // clear any pushed state to ensure a "clean" reload
        window.location.reload();
        break;
      case "dbot/refresh-project-status":
        this.http
          .get<GetProjectResponse>(`project/${this.projectId}`)
          .then((res) => {
            this.setProject(res.data, { syncDocuments: true });
            dispatch(refreshProjectStatusSuccessEvent());
          })
          .catch((err) => {
            dispatch([showError({ key: "dbot.refreshProjectStatusFailure" })]);
            dispatch(refreshProjectStatusFailureEvent(err));
          });
        break;
      case "dbot/explore-articles-of-association":
        this.http
          .post(`/articleofassociation/categorize/${this.projectContext.orgNo}`, event.articles)
          .then((res) => res.data)
          .then((data) => dispatch({ type: "dbot/explore-articles-of-association-success", data }))
          .catch((error) =>
            dispatch({ type: "dbot/explore-articles-of-association-error", error }),
          );
        break;
      case "dbot/add-documents": {
        const existingTypes = this.documents.map((d) => d.type);
        this.save(
          event.documentTypes
            .filter((dt) => dt === DocumentType.custom_document || !existingTypes.includes(dt))
            .map((dt) => ({
              type: "document-added",
              documentType: dt,
              customDocumentType: event.customDocumentType,
              customDocumentTitle: event.customDocumentTitle,
              customDocumentInitialRecipients: event.customDocumentInitialRecipients,
            })),
          event.onDone,
        );
        break;
      }
      case "dbot/remove-documents": {
        const toRemove = this.documents.filter((d) => event.documentTypes.includes(d.type));
        for (const doc of toRemove) {
          this.cleanupDocument(doc.documentId);
          removeDocument(this.documents, doc.documentId);
          this.save([{ type: "document-removed", documentId: doc.documentId }]);
        }
        break;
      }
      case "dbot/add-decisions":
        this.save(
          event.decisions.map((d) => ({ type: "decision-added", ...d, ...event.options })),
          event.onDone,
        );
        break;
      case "dbot/copy-decisions":
        this.save(
          [
            {
              type: "decisions-copied",
              sourceProjectId: event.projectId,
              sourceDecisionIds: event.decisions,
              documentId: event.options?.documentId,
            },
          ],
          event.onDone,
        );
        break;
      case "dbot/remove-decision":
        this.removeDecision(event.decisionId, event.onSuccess);
        break;
      case "dbot/register-new-signer":
        this.http
          .post<CompanyRole>(`/project/${this.projectId}/register-signer`, {
            name: event.name,
            email: event.email,
          })
          .then((res) => {
            dispatch(registerNewSignerSuccessEvent(res.data));
          })
          .catch(() => {
            dispatch(registerNewSignerFailureEvent({ key: "decisions.newSigner.saveError" }));
          });
        break;
      case "dbot/download-signed-document":
        this.http
          .get(
            `document/download-signed-project-documents?ref=${event.documentId}&orgNo=${this.orgNo}`,
            {
              headers: { Accept: "*/*" },
              responseType: "blob",
            },
          )
          .then((response: AxiosResponse) => {
            downloadFile(response.data, getFilenameFromHeaders(response.headers));
          })
          .catch(() => {
            dispatch([showGeneralError()]);
          });
        break;
      case "dbot/download-signed-subscription-forms":
        this.http
          .get(
            `document/download-signed-subscription-forms?ref=${event.documentId}&orgNo=${this.orgNo}`,
            {
              headers: { Accept: "*/*" },
              responseType: "blob",
            },
          )
          .then((response: AxiosResponse) => {
            downloadFile(response.data, getFilenameFromHeaders(response.headers));
          })
          .catch(() => {
            dispatch([showGeneralError()]);
          });
        break;
      case "dbot/get-signing-status":
        this.documentApiClient
          .getSigningStatus({ documentId: event.documentId, orgNo: this.orgNo })
          .then((data) => {
            dispatch(getSigningStatusSuccessEvent(event.documentId, data));
            dispatch(fetchDecisionStatusEvent());
          })
          .catch(() =>
            dispatch(
              getSigningStatusFailureEvent(event.documentId, {
                key: "dbot.getSigningStatusFailure",
              }),
            ),
          );
        break;
      case "dbot/fetch-increase-of-share-capital-subscribers-allocation":
        this.http
          .get<ShareAllocationRoundsResponse>(
            `/project/${this.projectId}/increaseOfShareCapitalAllocation`,
          )
          .then((res) => {
            dispatch(fetchIncreaseOfShareCapitalSubscribersAllocationSuccessEvent(res.data));
          })
          .catch(() =>
            dispatch(
              fetchIncreaseOfShareCapitalSubscribersAllocationFailureEvent({
                key: "dbot.fetchIncreaseOfShareCapitalSubscribersAllocationFailure",
              }),
            ),
          );
        break;
      case "dbot/save-share-allocation":
        this.http
          .post(`/project/${this.projectId}/increaseOfShareCapitalAllocation`, {
            allocationRounds: event.allocationRounds,
            isAllocationCompleted: event.isAllocationCompleted,
          })
          .then(() => {
            dispatch(saveShareAllocationSuccessEvent(event.isAllocationCompleted));
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([
              showError({
                key: "dbot.summary.subscribersAllocation.saveError",
              }),
            ]);
            dispatch(
              saveShareAllocationFailureEvent({
                key: "dbot.summary.subscribersAllocation.saveError",
              }),
            );
          });
        break;
      case "dbot/get-subscription-status":
        this.documentApiClient
          .getSubscriptionStatus({ documentId: event.documentId, orgNo: this.orgNo })
          .then((data) => {
            dispatch(refreshProjectStatusEvent());
            dispatch(getSubscriptionStatusSuccessEvent(event.documentId, data));
          })
          .catch(() =>
            dispatch(
              getSubscriptionStatusFailureEvent(event.documentId, {
                key: "dbot.getSubscriptionStatusFailure",
              }),
            ),
          );
        break;
      case "dbot/get-viewing-status":
        this.http
          .get<GetDocumentViewingStatusResponse>(
            `/document/check-viewing-status/${this.projectId}/${event.documentId}`,
          )
          .then((res) => {
            this.updateViewingStatus(event.documentId, res.data.statusForViewers);
            dispatch(getViewingStatusSuccessEvent(event.documentId, res.data));
          })
          .catch(() =>
            dispatch(
              getViewingStatusFailureEvent(event.documentId, {
                key: "dbot.getViewingStatusFailure",
              }),
            ),
          );
        break;
      case "dbot/send-document-for-viewing":
        this.http
          .post(
            "/document/send-for-viewing",
            sendDocumentsForViewingRequest({
              organizationnumber: this.projectContext.orgNo,
              projectId: this.projectId,
              documentTypes: this.documents
                .filter((d) => d.documentId === event.documentId)
                .map((d) => d.type),
              documentId: event.documentId,
              emailBody: event.emailBody,
              authMethod: event.authMethod,
              password: event.password,
              recipients: event.recipients,
            }),
          )
          .then(() => {
            dispatch([
              showSuccess({ key: "dbot.sendDocumentForViewingSuccess" }, { autoHide: true }),
            ]);
            dispatch(sendDocumentForViewingSuccessEvent(event.documentId, event.isResend));
            dispatch(refreshProjectStatusEvent());
            event.onSuccess?.();
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForViewingFailure" })]);
            dispatch(
              sendDocumentForViewingFailureEvent(event.documentId, {
                key: "dbot.sendDocumentForViewingFailure",
              }),
            );
            event.onFailure?.();
          });
        break;
      case "dbot/resend-notice-of-board-meeting":
        this.http
          .post(
            "/document/resend-notice-board-meeting",
            resendNoticeOfBoardMeetingRequest({
              organizationnumber: this.projectContext.orgNo,
              projectId: this.projectId,
              emailBody: event.emailBody,
            }),
          )
          .then(() => {
            dispatch([
              showSuccess({ key: "dbot.sendDocumentForViewingSuccess" }, { autoHide: true }),
            ]);
            dispatch(resendNoticeOfBoardMeetingSuccessEvent(event.document));
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForViewingFailure" })]);
            dispatch(
              resendNoticeOfBoardMeetingFailureEvent(event.document, {
                key: "dbot.sendDocumentForViewingFailure",
              }),
            );
          });
        break;
      case "dbot/resend-notice-to-updated-recipient":
        this.http
          .post(`/document/resend-for-viewing/${event.documentId}/${this.orgNo}`, event.data)
          .then(() => {
            if (event.data.companyroleId) {
              applyCompanyRoleEmails(this.projectContext, [
                { companyroleId: event.data.companyroleId, email: event.data.recipientEmail },
              ]);
            }
            dispatch({
              ...event,
              type: "dbot/resend-notice-to-updated-recipient-result",
              result: ok(),
            });
          })
          .catch(() => {
            dispatch({
              ...event,
              type: "dbot/resend-notice-to-updated-recipient-result",
              result: failure({ key: "dbot.sendDocumentForViewingFailure" }),
            });
          });
        break;
      case "dbot/send-subscription-form-for-signing":
        this.http
          .post(
            "/document/send-subscription-form-for-signing",
            mapToSendForSigningRequest(this.projectId, event),
          )
          .then(() => {
            dispatch([
              showSuccess({ key: "dbot.sendDocumentForViewingSuccess" }, { autoHide: true }),
            ]);
            dispatch(sendSubscriptionFormForSigningResult(event, ok()));
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForSigningFailure" })]);
            dispatch(sendSubscriptionFormForSigningResult(event, failure()));
          });
        break;
      case "dbot/send-document-for-signing":
        this.http
          .post(
            event.document.type === DocumentType.custom_document
              ? "document/send-custom-document-for-signing"
              : "/document/send-for-signing",
            mapToSendForSigningRequest(
              this.projectId,
              event,
              event.signingSettings,
              event.emailBody,
            ),
          )
          .then(() => {
            dispatch(
              sendDocumentForSigningResultEvent(
                event,
                ok(null),
                event.emailBody,
                this.projectContext.userPrefs,
              ),
            );
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForSigningFailure" })]);
            dispatch(sendDocumentForSigningResultEvent(event, failure(), event.emailBody));
          });
        break;
      case "dbot/send-multiple-documents-for-signing":
        this.http
          .post(
            "/document/send-multiple-for-signing",
            mapToSendMultipleForSigningRequest(
              this.projectId,
              event.documents,
              event.signingSettings,
              event.emailBody,
            ),
          )
          .then(() => {
            dispatch(sendMultipleDocumentsForSigningResultEvent(ok(null)));
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForSigningFailure" })]);
            dispatch(sendMultipleDocumentsForSigningResultEvent(failure()));
          });
        break;
      case "dbot/resend-document-for-signing":
        this.documentApiClient
          .resendForSigning({ signedDocumentId: event.signedDocumentId }, event)
          .then((data) => {
            dispatch({
              type: "dbot/resend-document-for-signing-result",
              documentId: event.documentId,
              signedDocumentId: event.signedDocumentId,
              result: ok(data),
            });
          })
          .catch(() => {
            dispatch({
              type: "dbot/resend-document-for-signing-result",
              documentId: event.documentId,
              signedDocumentId: event.signedDocumentId,
              result: failure({ key: "dbot.summary.resendFailure" }),
            });
          });
        break;
      case "dbot/update-company-role-emails":
        this.updateCompanyRoles(event.orgNo, event.items)
          .then(() =>
            dispatch(
              updateCompanyRoleEmailsSuccessEvent(event.orgNo, event.items, {
                key: "dbot.recipient-email-updated.company-role",
              }),
            ),
          )
          .catch(() =>
            dispatch(
              updateCompanyRoleEmailsFailureEvent(event.orgNo, event.items, {
                key: "dbot.updateCompanyRoleEmailsFailure",
              }),
            ),
          );
        break;
      case "dbot/update-decision-texts":
        {
          const decision = this.decisions.find((d) => d.decisionId === event.decisionId);
          if (decision && event.texts.length) {
            event.texts.forEach((t) => {
              replaceLocalizedTextForDecision(t, decision);
            });
            this.save([
              {
                type: "decision-texts-changed",
                decisionId: event.decisionId,
                texts: decision.decisionTexts,
              },
            ]);
          }
        }
        break;
      case "dbot/update-decision-intro-texts":
        {
          const decision = this.decisions.find((d) => d.decisionId === event.decisionId);
          if (decision && event.texts.length) {
            event.texts.forEach((t) => {
              replaceLocalizedIntroTextForDecision(t, decision);
            });
            this.save([
              {
                type: "decision-intro-texts-changed",
                decisionId: event.decisionId,
                texts: decision.decisionIntroTexts,
              },
            ]);
          }
        }
        break;
      case "dbot/fetch-decision-status":
        this.http
          .get<DecisionStatusResponse>(`/project/${this.projectId}/decisionStatus`)
          .then((res) => {
            dispatch(fetchDecisionStatusResultEvent(res.data));
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.summary.fetchDecisionStatusFailure" })]);
          });
        break;
      case "dbot/update-follow-up-status":
        this.http
          .post(
            `/project/${this.projectId}/decisionFollowUp/${event.decisionId}`,
            event.followUpData,
          )
          .then((res) => {
            dispatch(updateFollowUpStatusResultEvent(res.data));
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.summary.fetchDecisionStatusFailure" })]);
          });
        break;
      case "dbot/apply-decision":
        this.http
          .post(`/project/${this.projectId}/apply-decision`, event.subscription ?? {}, {
            params: {
              orgNo: event.orgNo,
              decisionId: event.decisionId,
            },
          })
          .then(() => {
            if (event.decisionType !== DecisionType.incorporation) {
              dispatch(applyDecisionSuccessEvent(event));
              dispatch({ type: "dbot/reload-project" });
              return;
            }
            this.http
              .get<DecisionStatusResponse>(`/project/${this.projectId}/decisionStatus`)
              .then((res) => {
                dispatch(fetchDecisionStatusResultEvent(res.data));
                dispatch(applyDecisionSuccessEvent(event));
                dispatch(refreshProjectStatusEvent());
              })
              .catch(() => {
                dispatch(applyDecisionSuccessEvent(event));
                dispatch({ type: "dbot/reload-project" });
              });
          })
          .catch((e) => {
            const response = isBadRequestError(e)
              ? (e.response?.data as TransactionErrorResponse | undefined)
              : undefined;
            const genericFailure: TMessage = { key: "dbot.summary.applyDecisionFailure" };
            const message =
              response?.validationError !== undefined
                ? transactionValidationErrorMessage(response.validationError, genericFailure)
                : (exerciseApplyErrorMessage(isBadRequestError(e) ? e.response?.data : undefined) ??
                  genericFailure);
            if (response?.replayError !== undefined) {
              dispatch([
                {
                  type: "show-dialog",
                  dialog: {
                    type: "replay-error",
                    orgNo: this.orgNo,
                    error: response.replayError,
                  },
                },
              ]);
            } else {
              dispatch([showError(message)]);
            }
            dispatch(applyDecisionFailureEvent(event, message));
          });
        break;
      case "dbot/send-to-br":
        this.http
          .post(`/project/${this.projectId}/send-to-br`, {
            signers: event.signers,
            decisions: event.decisions,
          })
          .then(() => {
            dispatch(fetchDecisionStatusEvent());
            dispatch(sendToBRSuccessEvent(event));
          })
          .catch(() => {
            dispatch(sendToBRFailureEvent(event, { key: "dbot.summary.sendToBRFailure" }));
          });
        break;
      case "dbot/send-incorporation-to-br":
        this.http
          .post(`/project/${this.projectId}/dse/incorporation/submit`)
          .then(() => {
            dispatch(fetchDecisionStatusEvent());
            dispatch(sendToBRSuccessEvent(event));
          })
          .catch((err) => {
            const code = err?.response?.data?.code as string | undefined;
            dispatch(sendToBRFailureEvent(event, dseSubmitErrorMessage(code)));
          });
        break;
      case "dbot/prekontroll-incorporation":
        this.prekontrollFailure = undefined;
        this.http
          .post(`/project/${this.projectId}/dse/incorporation/prekontroll`, {
            orgNo: event.orgNo,
          })
          .then(() => {
            dispatch(prekontrollIncorporationSuccessEvent(event.orgNo, event.decisionId));
          })
          .catch((err) => {
            const data = err?.response?.data;
            const code = data?.code as string | undefined;
            // Prekontroll validation items: either BR's feillogg descriptions or backend local codes.
            const errors = Array.isArray(data?.errors)
              ? (data.errors as DsePrekontrollErrorItem[])
              : undefined;
            dispatch(
              prekontrollIncorporationFailureEvent(
                event.orgNo,
                event.decisionId,
                dsePrekontrollFailureMessage(code),
                errors,
                code,
              ),
            );
          });
        break;
      case "dbot/prekontroll-incorporation-result": {
        // Gated send-for-signing continuation: only proceed to the signing flow once BR's
        // prekontroll passes. On failure, we stop here (hard block) — the incorporation form
        // stores the errors via forwardEvent and renders them inline on the decision.
        const pendingSigning = this.pendingSendForSigningPrekontroll;
        if (pendingSigning !== undefined && pendingSigning.decisionId === event.decisionId) {
          this.pendingSendForSigningPrekontroll = undefined;
          if (isOk(event.result)) {
            // onSuccess re-runs fetch-document-signers, which keeps the button spinner pending
            // until the review dialog opens.
            dispatch(pendingSigning.onSuccess);
          } else {
            // Stop the button spinner and flag the incorporation decision invalid so the
            // established validation-error treatment (panel indicator) kicks in. The summary
            // shows the prekontroll message instead of the generic validation line — our own
            // validation passed, it is BR that turned the melding down.
            this.documentSigners.set(pendingSigning.contextDocumentId, failure());
            this.invalidDecisions = new Set([...this.invalidDecisions, event.decisionId]);
            this.prekontrollFailure = {
              decisionId: event.decisionId,
              message: dsePrekontrollFailureMessageWithDecisionLink(event.code),
              isSystemUnavailable: isDseSystemUnavailable(event.code),
            };
          }
        }
        break;
      }
      case "dbot/create-merger-counterpart":
        this.http
          .post(`/project/${this.projectId}/merger-counterpart`, { decisionId: event.decisionId })
          .then(() => {
            dispatch(createMergerCounterpartSuccessEvent());
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() =>
            dispatch(
              createMergerCounterpartFailureEvent({
                key: "dbot.summary.mergerCounterpart.create-failed",
              }),
            ),
          );
        break;
      case "dbot/check-incorporation-status":
        logDebug("ProjectController::check-incorporation-status GET", {
          decisionId: event.decisionId,
          url: `/project/${this.projectId}/dse/incorporation/status`,
        });
        this.http
          .get<DseStatusResponse>(`/project/${this.projectId}/dse/incorporation/status`)
          .then((res) => {
            logDebug("ProjectController::check-incorporation-status response", res.data);
            dispatch(checkIncorporationStatusSuccessEvent(event.decisionId, res.data));
            if (res.data.response?.kind === "APPROVED") {
              dispatch(fetchDecisionStatusEvent());
            }
          })
          .catch((err) => {
            logDebug(
              "ProjectController::check-incorporation-status error",
              err?.response?.status,
              err,
            );
            dispatch(
              checkIncorporationStatusFailureEvent(event.decisionId, {
                key: "dbot.summary.sendToBR.incorporation.checkStatusFailure",
              }),
            );
          });
        break;
      case "dbot/simulate-br-response":
        this.http
          .post(`/project/${this.projectId}/dse/incorporation/vedtak-document`, {
            kind: event.kind,
          })
          .then(() => {
            dispatch(checkIncorporationStatusEvent(event.decisionId));
          })
          .catch(() => {
            dispatch(
              checkIncorporationStatusFailureEvent(event.decisionId, {
                key: "dbot.summary.sendToBR.incorporation.simulate.failure",
              }),
            );
          });
        break;
      case "dbot/fetch-incorporation-activation-info":
        Promise.all([
          this.http.get<ListAdminAccessResponse>(`/company-settings/${this.orgNo}/admin-access`),
          this.http.get<EnterpriseAccountsForUserResponse>(`/user/enterprise-accounts`),
        ])
          .then(([adminAccess, enterpriseAccounts]) => {
            dispatch(
              fetchIncorporationActivationInfoSuccessEvent({
                admins: adminAccess.data.admins.map(({ id, name }) => ({ id, name })),
                enterpriseAccounts: enterpriseAccounts.data.enterpriseAccounts.map(
                  ({ id, name }) => ({ id, name }),
                ),
              }),
            );
          })
          .catch(() => {
            dispatch(
              fetchIncorporationActivationInfoFailureEvent({
                key: "dbot.summary.fetchDecisionStatusFailure",
              }),
            );
          });
        break;
      case "dbot/skip-send-to-br":
        this.http
          .post(`/project/${this.projectId}/skip-send-to-br`, null, {
            params: {
              orgNo: event.orgNo,
              decisionId: event.decisionId,
            },
          })
          .then(() => {
            dispatch([
              skipSendToBRResultEvent(event.orgNo, event.decisionId),
              fetchDecisionStatusEvent(),
            ]);
          })
          .catch(() => {
            dispatch([skipSendToBRResultEvent(event.orgNo, event.decisionId), showGeneralError()]);
          });
        break;
      case "dbot/save-participation":
        this.http
          .post(`/project/${this.projectId}/generalMeetingParticipation`, {
            createParticipation: event.createParticipation,
            updateParticipation: event.updateParticipation,
          })
          .catch(() => {
            dispatch([
              showError({
                key: "documents.attendingShareholders.participationSaveError",
              }),
            ]);
          });
        break;
      case "dbot/upload-attachments":
        uploadAttachments(
          this.projectId,
          event,
          this.initializationParams.fileUploadConfig,
          dispatch,
          this.http,
          this.documents,
          this.decisions,
        );
        break;
      case "dbot/delete-attachment":
        deleteAttachment(
          this.projectId,
          event,
          dispatch,
          this.http,
          this.documents,
          this.decisions,
        );
        break;
      case "dbot/keep-original-format": {
        const attachment = this.findAttachment(event.attachmentId);
        if (attachment) {
          keepOriginalFormat(this.projectId, attachment, dispatch, this.http);
        }
        break;
      }
      case "dbot/download-attachment":
        downloadAttachment({
          projectId: this.projectId,
          attachmentId: event.attachmentId,
          http: this.http,
        })
          .then(({ data, filename }) => downloadFile(data, filename))
          .catch(() => dispatch([showGeneralError()]));
        break;
      case "dbot/open-attachment-preview":
        this.handleOpenAttachmentPreview(event);
        break;
      case "dbot/open-attachment": {
        if (this.isDownloadingAttachment) {
          return;
        }
        const existingAttachment = this.findAttachment(event.attachmentId);
        if (event.filename) {
          const file: FilePreviewProps["file"] = {
            url: projectApi.downloadAttachmentUrl({
              projectId: this.projectId,
              attachmentId: event.attachmentId,
            }),
          };
          this.dispatch([
            {
              type: "preview-file",
              filename: event.filename,
              file,
              conversion: this.createAttachmentConversionModel(
                event.attachmentId,
                event.filename,
                existingAttachment?.documentConversionId,
                existingAttachment?.isConverting,
              ),
            },
          ]);
        } else {
          this.isDownloadingAttachment = true;
          downloadAttachment({
            projectId: this.projectId,
            attachmentId: event.attachmentId,
            http: this.http,
          })
            .then(({ data, filename }) => {
              this.dispatch([
                {
                  type: "preview-file",
                  filename,
                  file: { data },
                  conversion: this.createAttachmentConversionModel(
                    event.attachmentId,
                    filename ?? "",
                    existingAttachment?.documentConversionId,
                    existingAttachment?.isConverting,
                  ),
                },
              ]);
            })
            .catch(() => dispatch([showGeneralError()]))
            .finally(action(() => (this.isDownloadingAttachment = false)));
        }
        break;
      }
      case "dbot/fetch-general-meeting-votes":
        this.http
          .get(`/project/${this.projectId}/general-meeting-voting`)
          .then((response) => dispatch(fetchVotingResultEvent(response.data)))
          .catch(() => {
            dispatch([showError({ key: "documents.generalMeetingVoting.fetchError" })]);
          });
        break;
      case "dbot/save-general-meeting-votes":
        if (event.voteType === "decision") {
          this.http
            .post(
              `/project/${this.projectId}/general-meeting-voting/decision/${event.decisionId}`,
              event.votes,
            )
            .then(() => dispatch(saveGeneralMeetingVotesSuccessEvent()))
            .catch(() => {
              dispatch(saveGeneralMeetingVotesErrorEvent());
            });
        } else {
          this.http
            .post(
              `/project/${this.projectId}/general-meeting-voting/${event.voteType}`,
              event.votes,
            )
            .then(() => dispatch(saveGeneralMeetingVotesSuccessEvent()))
            .catch(() => {
              dispatch(saveGeneralMeetingVotesErrorEvent());
            });
        }
        break;
      case "dbot/search-companies":
        if (event.input.length > 2) {
          this.http
            .get<SuggestedCompany[]>(`/companysearch`, { params: { q: event.input } })
            .then((response) => dispatch(searchCompaniesResultEvent(event.source, response.data)))
            .catch(() => {
              dispatch(searchCompaniesResultEvent(event.source));
            });
        } else {
          dispatch(searchCompaniesResultEvent(event.source, []));
        }
        break;
      case "dbot/search-matrikkel-address":
        if (event.input.trim().length >= 3) {
          this.http
            .get<MatrikkelAddress[]>(`/matrikkel/address-search`, { params: { q: event.input } })
            .then((response) =>
              dispatch(searchMatrikkelAddressResultEvent(event.source, response.data)),
            )
            .catch(() => {
              dispatch(searchMatrikkelAddressResultEvent(event.source));
            });
        } else {
          dispatch(searchMatrikkelAddressResultEvent(event.source, []));
        }
        break;
      case "dbot/verify-matrikkel-address":
        this.http
          .get<MatrikkelAddressVerification>(`/matrikkel/address-verify`, {
            params: { address: event.address, postalCode: event.postalCode },
          })
          .then((response) =>
            dispatch(verifyMatrikkelAddressResultEvent(event.source, response.data)),
          )
          .catch(() => {
            dispatch(verifyMatrikkelAddressResultEvent(event.source));
          });
        break;
      case "dbot/check-company-name":
        this.http
          .get<CompanyNameCheck>(`/company-name-check`, { params: { name: event.name } })
          .then((response) => dispatch(checkCompanyNameResultEvent(event.source, response.data)))
          .catch(() => {
            dispatch(checkCompanyNameResultEvent(event.source));
          });
        break;
      case "dbot/check-provider-validity":
        this.http
          .get<ProviderValidity>(
            event.role === "auditor" ? `/auditor-validity` : `/accountant-validity`,
            { params: { orgNo: event.organisationNumber } },
          )
          .then((response) =>
            dispatch(checkProviderValidityResultEvent(event.source, response.data)),
          )
          .catch(() => {
            dispatch(checkProviderValidityResultEvent(event.source));
          });
        break;
      case "dbot/search-municipalities":
        this.http
          .get<Municipality[]>(`/municipalities`, { params: { q: event.input } })
          .then((response) =>
            dispatch(searchMunicipalitiesResultEvent(event.source, response.data)),
          )
          .catch(() => {
            dispatch(searchMunicipalitiesResultEvent(event.source));
          });
        break;
      case "dbot/search-nace":
        if (event.input.trim().length >= NACE_SEARCH_MIN_QUERY_LENGTH) {
          this.http
            .get<NaceSearchResult[]>(`/nace`, { params: { q: event.input } })
            .then((response) => dispatch(searchNaceResultEvent(event.source, response.data)))
            .catch(() => {
              dispatch(searchNaceResultEvent(event.source));
            });
        } else {
          dispatch(searchNaceResultEvent(event.source, []));
        }
        break;
      case "dbot/update-board-meeting-recipients": {
        this.updateCompanyRoles(
          this.orgNo,
          getUpdatedRoles(this.projectContext.initialState.roles, event.items),
        )
          .then(() => dispatch(updateBoardMeetingRecipientsSuccessEvent(event.items)))
          .catch(() =>
            dispatch(
              updateBoardMeetingRecipientsFailureEvent({
                key: "dbot.updateBoardMeetingRecipientsFailure",
              }),
            ),
          );
        break;
      }

      case "dbot/update-shareholder-email":
        this.updateShareholderEmail(event.shareholderId, event.email)
          .then(() =>
            event.onFulfilled(ok({ key: "dbot.recipient-email-updated.shareholder" }), event.email),
          )
          .catch(() => {
            event.onFulfilled(failure({ key: "error.update-email" }));
          });
        break;

      case "dbot/update-shareholder-email-batch":
        this.updateShareholderEmailBatch(event.updates)
          .then((res) => {
            when(event.onSuccess?.(res), (events) => this.dispatch(events));
          })
          .catch(() => {
            when(event.onFailure?.(), (events) => this.dispatch(events));
          });
        break;

      case "dbot/validate-project":
        this.validateProject(event);
        break;
      case "dbot/reopen-document":
        this.http
          .post(
            `/document/reopen-locked-document/${event.documentId}/${this.orgNo}${event.isCancelSignatureRequest ? "?isCancelSignatureRequest=true" : ""}`,
            {},
          )
          .then(() => {
            dispatch(reopenDocumentResultEvent(event.documentId, ok()));
            dispatch([showSuccess({ key: "dbot.action.reopen.success" })]);
          })
          .catch(() => {
            dispatch(reopenDocumentResultEvent(event.documentId, failure()));
          });
        break;
      case "dbot/send-for-review":
        this.documentApiClient
          .sendForReview({ documentId: event.documentId, orgNo: this.orgNo, data: event })
          .then(() => {
            dispatch({ type: "dbot/send-for-review-result", result: ok() });
            dispatch(refreshProjectStatusEvent());
            dispatch({
              type: "dbot/toggle-document-panel",
              documentId: reviewDocumentId,
              expanded: true,
            });
          })
          .catch(() => {
            dispatch({
              type: "dbot/send-for-review-result",
              result: failure(),
            });
          });
        break;
      case "dbot/autosign-document":
        this.state = { type: "loading", message: { translated: "Autosigning documents" } };
        this.http
          .post(`/document/autosign/${this.projectId}/${event.document.documentId}`, {})
          .then(() => dispatch({ type: "dbot/reload-project" }))
          .catch(() => {
            dispatch({ type: "dbot/reload-project" });
          });
        break;
      case "dbot/autoapprove-send-to-br":
        this.state = {
          type: "loading",
          message: { translated: "Autoapproving decision sent to BR" },
        };
        this.http
          .post(`/project/${this.projectId}/autoapprove-send-to-br/${event.decisionId}`, {})
          .then(() => dispatch({ type: "dbot/reload-project" }))
          .catch(() => {
            dispatch({ type: "dbot/reload-project" });
          });
        break;
      case "dbot/navigate-to-project-list":
        navigateToProjectList(this.orgNo, this.history);
        break;
      case "dbot/edit-project-name":
        this.dispatch([
          {
            type: "editProjectName",
            name: new StringFieldModel({ value: this.name, required: true }),
            save: (name: string) => dispatch({ type: "dbot/save-project-name", name: name }),
          },
        ]);
        break;
      case "dbot/save-project-name":
        if (event.name !== this.project.name && event.name !== "") {
          this.project.name = event.name;
          this.save([{ type: "project-name-changed", name: event.name }]);
        }
        this.dispatch([{ type: "closeDialog" }]);
        break;
      case "dbot/open-mobile-document-preview":
        this.setViewState({ isMobileDocumentPreviewOpen: true });
        break;
      case "dbot/close-mobile-document-preview":
        this.setViewState({ isMobileDocumentPreviewOpen: false });
        break;
      case "dbot/duplicate-project":
        if (this.isDuplicateGeneralMeeting || this.isDuplicateBoardMeeting) {
          this.dispatch([
            {
              type: "new-meeting",
              orgNo: this.orgNo,
              meetingType: this.isDuplicateBoardMeeting
                ? MeetingType.BOARD_MEETING
                : MeetingType.GENERAL_MEETING,
              projectName: { translated: event.name },
              projectId: this.projectId,
            },
          ]);
        } else {
          this.dispatch(
            showDuplicateProjectDialog({
              projectId: this.projectId,
              projectName: event.name,
              companies: this.projectContext.availableCompanies,
            }),
          );
        }
        break;
      case "dbot/delete-project":
        this.state = { type: "delete-project", alert: event.alert, onSuccess: event.onSuccess };
        break;
      case "dbot/cancel-delete-project":
        this.state = readyState();
        break;
      case "dbot/confirm-delete-project":
        if (this.state.type === "delete-project") {
          this.state.deleteResult = pending();
          const onSuccess = this.state.onSuccess;
          this.http
            .delete(`/project/${this.projectId}`)
            .then(() => dispatch(onSuccess ?? { type: "dbot/navigate-to-project-list" }))
            .catch(() => {
              dispatch([showGeneralError()]);
            });
        }
        break;
      case "dbot/toggle-fullscreen":
        this.setViewState({
          displayMode: this.displayMode === "fullscreen" ? "normal" : "fullscreen",
        });
        break;
      case "dbot/toggle-screen-share-mode":
        this.setViewState({
          displayMode: this.displayMode === "screen-share" ? "normal" : "screen-share",
        });
        break;
      case "dbot/open-details-panel":
        this.setViewState({ detailsPanel: event.panel });
        this.initEditDecision();
        break;
      case "dbot/close-details-panel":
        this.setViewState({ detailsPanel: undefined, detailsPanelMaximized: false });
        this.cleanupEditDecision();
        this.cleanupEditDocument();
        break;
      case "dbot/move-decision":
        this.moveDecision(event.decisionId, event.toDecisionId);
        break;
      case "dbot/move-document-attachment":
        this.moveDocumentAttachment(event.documentId, event.attachmentId, event.toAttachmentId);
        break;
      case "dbot/move-decision-attachment":
        this.moveDecisionAttachment(event.decisionId, event.attachmentId, event.toAttachmentId);
        break;
      case "dbot/open-tab":
        if (this.visibleTabs.includes(event.tab)) {
          this.setViewState({ activeTab: event.tab });
          if (this.activeTab === "meetings") {
            this.dispatch({
              type: "dbot/toggle-document-panel",
              documentId: conductGeneralMeetingDocumentId,
              expanded: true,
            });
          }
        }
        break;
      case "dbot/toggle-document-panel":
        this.setViewState({
          selectedDocumentId: isNonDocumentTab(event.documentId)
            ? event.documentId === reviewDocumentId
              ? this.documents.find((d) => d.type === DocumentType.board_meeting)?.documentId
              : this.view.selectedDocumentId
            : event.expanded
              ? event.documentId
              : undefined,
          selectedDocumentPreview: event.documentId,
          selectedDocumentPreviewBaseVersion: event.previewBaseVersion,
          detailsPanel: updateDetailsPanelDocumentContext(
            event.documentId,
            this.detailsPanel,
            this.projectContext.currentDocuments,
          ),
          activeTab:
            event.documentId === conductGeneralMeetingDocumentId ? "meetings" : "documents",
        });
        break;
      case "dbot/toggle-maximize-details-panel":
        this.setViewState({ detailsPanelMaximized: !this.detailsPanelMaximized });
        break;
      case "dbot/electronic-general-meeting-update-status":
        this.electronicGMController.updateStatus(event);
        break;
      case "dbot/electronic-general-meeting-update-status-result":
        if (isOk(event.result)) {
          this.electronicGMController.updateMeeting(event.result.data);
        } else {
          dispatch([showGeneralError()]);
        }
        break;
      case "dbot/update-general-meeting":
        this.project.electronicGeneralMeeting = event.meeting;
        break;
      case "dbot/project-validation-failed":
        this.validationErrors = [event.validationError];
        break;

      case "dbot/change-state":
        this.state = event.state;
        break;

      case "dbot/update-state":
        runInAction(() => event.update(this.state));
        break;

      case "dbot/save-selected-batch-companies":
        this.save([{ type: "companies-changed", companies: event.companies }], (err) =>
          err ? event.onFailure : event.onSuccess,
        );
        break;
      case "dbot/fetch-destination-folders":
        this.http
          .get<DestinationFoldersResponse>(
            `/portfolio/signatures/destination-folders/${event.orgNo ?? this.orgNo}?projectId=${this.projectId}`,
          )
          .then((res) => {
            this.dispatch(event.onSuccess(res.data));
          })
          .catch(() => this.dispatch(event.onFailure));
        break;

      case "dbot/fetch-document-signers":
        this.documentSigners.set(event.documentId, pending());
        this.http
          .get<GetProjectDocumentSignersResponse>(
            `/project/${this.projectId}/signers/${event.documentId}`,
          )
          .then(
            action((res) => {
              this.documentSigners.set(event.documentId, ok(res.data));
              this.dispatch(event.onSuccess(res.data));
            }),
          )
          .catch(
            action(() => {
              this.documentSigners.set(event.documentId, failure());
              this.dispatch(event.onFailure);
            }),
          );
        break;

      case "dbot/fetch-signable-documents":
        this.http
          .get<GetSignableDocumentsResponse>(`/project/${this.projectId}/signable-documents`)
          .then((res) => this.dispatch(event.onSuccess(res.data)))
          .catch(() => this.dispatch(event.onFailure));
        break;

      case "dbot/fetch-document-recipients":
        this.http
          .get<GetProjectDocumentRecipientsResponse>(
            `/project/${this.projectId}/recipients/${event.documentId}`,
          )
          .then((res) => this.dispatch(event.onSuccess(res.data)))
          .catch(() => this.dispatch(event.onFailure));
        break;

      case "dbot/confirm-remove-document":
        this.confirmRemoveDocument(event);
        break;

      case "dbot/open-change-permission-dialog":
        this.dispatch({
          type: "showChangePermissionDialog",
          title: this.name,
          objectId: toProjectReference(this.projectId),
          moduleName: ModuleName.DBOT,
          onUpdate: (permissions: ObjectPermission[]) =>
            (this.project.numberOfPermissions = permissions.length),
        });
        break;

      case "dbot/open-save-as-template-dialog": {
        const account = this.initializationParams.enterpriseAccountInfo;
        if (account !== undefined) {
          this.dispatch(
            showSaveProjectAsTemplateDialog({
              projectId: this.projectId,
              projectName: this.name,
              account: { id: account.enterpriseAccountId, name: account.accountName },
              ...projectTemplateItems(this.project, this.locale),
              referrer: this.history.location.pathname + this.history.location.search,
            }),
          );
        }
        break;
      }

      case "dbot/fetch-projects-for-copy":
        this.http
          .get<ProjectsForCopyResponse>(
            `/projects/${this.orgNo}/projects-for-copy/${event.projectId}`,
          )
          .then((res) => this.dispatch(event.onSuccess(res.data.projects)))
          .catch(() => this.dispatch(event.onFailure));
        break;

      case "dbot/close-dialog":
        this.summaryController.closeDialog();
        break;

      case "dbot/select-company":
        this.selectedCompany = event.orgNo;
        break;
      case "dbot/update-document-preview":
        this.updateDocumentPreviews(event);
        break;
      case "dbot/upload-document-blob":
        this.uploadDocumentBlob(event.projectDocumentId, event.orgNo, event.files);
        break;
      case "dbot/delete-document-blob":
        this.handleDeleteDocumentBlobEvent(event);
        break;
      case "dbot/open-document-blob":
        this.handleOpenDocumentBlobEvent(event.orgNo);
        break;
      case "dbot/update-uploading-attachment-props":
        this.handleUpdateFilename(event);
        break;
    }

    forwardEvent(
      event,
      this.decisions,
      this.documents,
      this.summaryController,
      this.electronicGMController,
      this.eventListeners,
    );
  }

  handleOpenAttachmentPreview(event: OpenAttachmentPreviewEvent) {
    this.scrollToId = event.attachmentId;
    //now, we need to reset the scrollToId so that the next time user clicks we
    // trigger the scroll in preview regardless of the scroll position in the preview tab
    setTimeout(
      action(() => (this.scrollToId = undefined)),
      0,
    );
  }

  handleOpenDocumentBlobEvent(orgNo: OpenDocumentBlobEvent["orgNo"]) {
    this.selectedCompany = this.projectContext.companies.find((c) => c.orgNo === orgNo)?.orgNo;
  }

  findAttachment(attachmentId: string) {
    const attachmentFromDocument = this.documents
      .flatMap((document) => document.attachments)
      .find((attachment) => attachment.id === attachmentId);
    if (attachmentFromDocument) {
      return attachmentFromDocument;
    }
    return this.decisions
      .flatMap((decision) => decision.attachments)
      .find((attachment) => attachment.id === attachmentId);
  }

  createAttachmentConversionModel(
    attachmentId: string,
    filename: string,
    documentConversionId?: string,
    isConverting?: boolean,
  ) {
    const model = new ConversionModel(
      this.http,
      { type: "attachmentId", id: attachmentId },
      filename,
      this.dispatch,
    );
    if (documentConversionId) {
      if (isConverting) {
        model.checkStatusInBackground(false, undefined, documentConversionId);
      } else {
        model.conversionResult = ok(documentConversionId);
      }
    }
    return model;
  }

  handleUpdateFilename(event: UpdateUploadingAttachmentPropsEvent) {
    const attachmentId = toAttachmentId(event.reference);
    if (!attachmentId) {
      return;
    }

    const shouldUpdateDocumentConversionId = "documentConversionId" in event;
    const shouldUpdateIsConverting = "isConverting" in event;
    const shouldUpdateOriginalFileType = "originalFileType" in event;

    this.decisions.forEach((d) => {
      d.attachments.forEach((attachment) => {
        if (attachment.id === attachmentId) {
          attachment.filename = event.filename;
          if (shouldUpdateDocumentConversionId) {
            attachment.documentConversionId = event.documentConversionId;
          }
          if (shouldUpdateIsConverting) {
            attachment.isConverting = event.isConverting;
          }
          if (shouldUpdateOriginalFileType) {
            attachment.originalFileType = event.originalFileType;
          }
        }
      });
    });

    this.documents.forEach((d) => {
      const index = d.attachments.findIndex((attachment) => attachment.id === attachmentId);
      if (index >= 0) {
        d.attachments[index] = {
          ...d.attachments[index],
          filename: event.filename,
          ...(shouldUpdateDocumentConversionId
            ? { documentConversionId: event.documentConversionId }
            : {}),
          ...(shouldUpdateIsConverting ? { isConverting: event.isConverting } : {}),
          ...(shouldUpdateOriginalFileType ? { originalFileType: event.originalFileType } : {}),
        };
      }
    });
  }

  handleDeleteDocumentBlobEvent({ onComplete = [], ...event }: DeleteDocumentBlobEvent) {
    deleteDocumentBlob({
      projectId: this.projectId,
      documentId: event.projectDocumentId,
      orgNo: event.orgNo,
      http: this.http,
      onSuccess: action((res) => {
        this.setProject(res.project);
        this.customDocumentUpload = this.customDocumentUpload.filter(
          (x) => !(x.documentId === event.projectDocumentId && x.orgNo === event.orgNo),
        );
        this.dispatch([showGeneralSuccess(), ...onComplete]);
      }),
      onError: () => {
        this.dispatch([showGeneralError(), ...onComplete]);
      },
    });
  }

  updateCompanyRoles(orgNo: string, items: UpdateCompanyRoleItem[]): Promise<void> {
    if (!items.length) {
      return Promise.resolve();
    }
    return this.http
      .post(`/company/update-companyrole-emails/${orgNo}`, items)
      .then(action(() => applyCompanyRoleEmails(this.projectContext, items)));
  }

  updateShareholderEmail(shareholderId: number, email: string): Promise<void> {
    return this.http
      .post(`/shareholder/update`, {
        shareholderId: shareholderId,
        fieldName: ShareholderField.email,
        newValue: email,
      })
      .then(action(() => applyShareholderEmail(this.projectContext, shareholderId, email)));
  }

  updateShareholderEmailBatch(
    updates: { shareholderId: number; email: string }[],
  ): Promise<UpdateShareholderEmailBatchResponse> {
    return this.http
      .post<UpdateShareholderEmailBatchResponse>(
        `/shareholder/email/batchUpdate`,
        updateShareholderEmailBatchRequest(updates),
      )
      .then(
        action((res) => {
          res.data.results.forEach((r) => {
            if (!r.validationError) {
              applyShareholderEmail(this.projectContext, r.shareholderId, r.email);
            }
          });
          return res.data;
        }),
      );
  }

  initDecision(decision: Decision, index?: number): DecisionAndForm<DecisionType> {
    const context = createDecisionContext(
      decision.decisionId,
      decision.decisionTexts,
      decision.decisionIntroTexts,
      decision.attachments,
      this,
    );
    const decisionDataProvider = () =>
      this.project.jsonData.decisions.find((x) => x.decisionId === decision.decisionId)!;
    const d: DecisionAndForm<DecisionType> = {
      type: decision.decisionData.type,
      decisionId: decision.decisionId,
      get decision() {
        return decisionDataProvider();
      },
      form: getHandler(decision.decisionData.type).initNewForm(
        this.dispatch,
        context,
        decision.decisionData,
        decision,
      ),
      decisionTexts: context.decisionTexts,
      decisionIntroTexts: context.decisionIntroTexts,
      attachments: context.attachments,
    };
    if (index === undefined) {
      this.decisions.push(d);
    } else {
      this.decisions.splice(index, 0, d);
    }

    const observedDecision = this.decisions.find((x) => x.decisionId === d.decisionId)!;

    this.reactionDisposers.set(
      d.decisionId,
      reaction(
        () => applyForm(observedDecision),
        (change) => {
          logDebug(`ProjectController: decision ${d.type} (${d.decisionId}) has changed`);
          this.save([change]);
        },
      ),
    );

    this.reactionDisposers.set(
      `${d.decisionId}/attachments`,
      reaction(
        () => applyDecisionAttachments(observedDecision),
        (change) => {
          logDebug(
            `ProjectController: decision ${d.type} (${d.decisionId}) attachments have changed`,
          );
          this.save([change]);
        },
      ),
    );

    this.reactionDisposers.set(
      `${d.decisionId}/init`,
      getHandler(observedDecision.type).initForm?.(observedDecision.form) ?? noop,
    );

    return d;
  }

  reinitDecision(decision: Decision) {
    const index = this.decisions.findIndex((d) => d.decisionId === decision.decisionId);
    if (index === -1) {
      return;
    }
    [
      decision.decisionId,
      `${decision.decisionId}/attachments`,
      `${decision.decisionId}/init`,
    ].forEach((key) => {
      this.reactionDisposers.get(key)?.();
      this.reactionDisposers.delete(key);
    });
    this.decisions.splice(index, 1);
    this.initDecision(decision, index);
  }

  removeDecision(decisionId: string, onSuccess?: DbotEvent) {
    this.state = removeDecisionState(decisionId, onSuccess);
  }

  moveDecision(decisionId: string, toDecisionId: string) {
    const decision = this.decisions.find((d) => d.decisionId === decisionId);
    if (decision) {
      const toIndex = this.decisions.findIndex((d) => d.decisionId === toDecisionId);
      this.decisions = move(this.decisions, decision, toIndex);
      this.save([{ type: "decision-moved", decisionId, toDecisionId }]);
    }
  }

  moveDocumentAttachment(documentId: string, attachmentId: string, toAttachmentId: string) {
    const document = this.documents.find((d) => d.documentId === documentId);
    if (!document) {
      return;
    }
    const attachments = movedById(document.attachments, attachmentId, toAttachmentId);
    if (attachments !== document.attachments) {
      document.attachments = attachments;
      this.save([{ type: "document-attachment-moved", documentId, attachmentId, toAttachmentId }]);
    }
  }

  moveDecisionAttachment(decisionId: string, attachmentId: string, toAttachmentId: string) {
    const decision = this.decisions.find((d) => d.decisionId === decisionId);
    if (!decision) {
      return;
    }
    const attachments = movedById(decision.attachments, attachmentId, toAttachmentId);
    if (attachments !== decision.attachments) {
      decision.attachments = attachments;
      this.save([{ type: "decision-attachment-moved", decisionId, attachmentId, toAttachmentId }]);
    }
  }

  *reloadGeneralMeeting() {
    try {
      this.project.electronicGeneralMeeting = yield this.http
        .get(`/general-meeting/${this.projectId}`)
        .then((r) => r.data);
    } catch (e) {
      this.dispatch([showGeneralError()]);
    }
  }

  get showConfirmRemoveDecisionDialog() {
    return isRemoveDecision(this.state) || isRemovingDecision(this.state);
  }

  confirmRemoveDecision() {
    if (!isRemoveDecision(this.state)) {
      return;
    }
    const onSuccess = this.state.onSuccess;
    this.state = removingDecisionState(this.state);

    this.reactionDisposers.get(this.state.decisionId)?.();
    this.reactionDisposers.delete(this.state.decisionId);

    const removed = removeDecisionById(this.decisions, this.state.decisionId);

    if (removed) {
      this.save([{ type: "decision-removed", decisionId: this.state.decisionId }]);
      this.dispatch({ type: "dbot/decision-removed", decisionId: this.state.decisionId });
      if (onSuccess) {
        this.dispatch(onSuccess);
      }
    }

    this.state = readyState();
  }

  cancelRemoveDecision() {
    this.state = readyState();
  }

  get sortedDocuments(): DocumentAndForm[] {
    const sorted = sortDocuments(this.project.jsonData.documents);
    return compact(sorted.map((s) => this.documents.find((d) => d.documentId === s.documentId)));
  }

  hasRecipientPreview(documentId: string, company: string, recipientId: RecipientId): boolean {
    return (
      this.documentRecipientPreviews[compact([documentId, company, recipientId]).join("/")] !==
      undefined
    );
  }

  getRecipientPreview(
    documentId: string,
    company: string,
    recipientId: RecipientId,
  ): Result<DocumentPreview> {
    return (
      this.documentRecipientPreviews[compact([documentId, company, recipientId]).join("/")] ??
      pending()
    );
  }

  setRecipientPreview(
    documentId: string,
    company: string,
    recipientId: RecipientId,
    preview: Result<DocumentPreview>,
  ) {
    this.documentRecipientPreviews[[documentId, company, recipientId].join("/")] = preview;
  }

  getIncorporationCompanyPreview(
    documentId: string,
    orgNo: string,
  ): Result<DocumentPreview> | undefined {
    return this.incorporationCompanyPreviews[[documentId, orgNo].join("/")];
  }

  setIncorporationCompanyPreview(
    documentId: string,
    orgNo: string,
    preview: Result<DocumentPreview>,
  ) {
    this.incorporationCompanyPreviews[[documentId, orgNo].join("/")] = preview;
  }

  updateDocumentPreviews(event: UpdateDocumentPreview) {
    event.updatedPreviews.forEach((u) => {
      const p = this.previews.find(
        (p) => p.documentId === u.projectDocumentId && p.orgNo === u.orgNo,
      );
      if (p) {
        p.content = u.preview;
        p.isPreviewInProgress = false;
      }
    });
  }

  uploadDocumentBlob(documentId: string, orgNo: string | undefined, files: File[]) {
    const progressKey = `${documentId}:${orgNo ?? ""}`;
    this.customDocumentUpload.push({ documentId, orgNo, files });
    this.customDocumentProgress.set(progressKey, 0);
    uploadCustomDocumentBlob({
      projectId: this.projectId,
      documentId,
      orgNo,
      files,
      fileUploadConfig: toPdfOrOfficeUploadConfig(this.projectContext.fileUploadConfig),
      dispatch: this.dispatch,
      http: this.http,
      onProgress: action((progress: number) => {
        this.customDocumentProgress.set(progressKey, progress);
      }),
      onConversionStart: action((model: ConversionModel) => {
        this.customDocumentConverting.push({ documentId, orgNo, model });
      }),
      onSuccess: action((res) => {
        this.setProject(res.project);
        this.customDocumentUpload = this.customDocumentUpload.filter(
          (x) => !(x.documentId === documentId && x.orgNo === orgNo),
        );
        this.customDocumentProgress.delete(progressKey);
        this.dispatch(showGeneralSuccess());
        this.selectedCompany = this.projectContext.companies.find((c) => c.orgNo === orgNo)?.orgNo;
      }),
      onError: action(() => {
        this.customDocumentUpload = this.customDocumentUpload.filter(
          (x) => !(x.documentId === documentId && x.orgNo === orgNo),
        );
        this.customDocumentProgress.delete(progressKey);
      }),
    });
  }

  get sortedDocumentPreviews(): TabInPreview[] {
    return this.sortedDocuments.map<DocumentTab>((d) => {
      const incorporationCompanies: ProjectCompany[] | undefined = (
        this.projectContext.jsonData.decisions.find(
          (d) => d.decisionData.type === DecisionType.incorporation,
        )?.decisionData as Incorporation
      )?.companyNames?.map<ProjectCompany>((name, index) => ({
        orgNo: `${index}`,
        name,
        isUnderIncorporation: true,
      }));
      const companies = incorporationCompanies || this.projectContext.companies;
      const selectedCompany =
        companies.find((c) => c.orgNo === this.selectedCompany) ?? companies[0];
      const selectedOrgNo = selectedCompany.orgNo;
      const companyIndex = companies.indexOf(selectedCompany);
      const nextCompany = next(companies, (c) => c.orgNo === selectedOrgNo)?.orgNo;
      const prevCompany = prev(companies, (c) => c.orgNo === selectedOrgNo)?.orgNo;
      const hasRecipientPreviews =
        getDocumentHandler(d.type).hasRecipientPreviews?.(d.form) ?? false;
      const recipients = getDocumentHandler(d.type).getRecipients?.(d.form, selectedOrgNo) ?? [];
      const selectedRecipientId =
        recipients.find((r) => r.id === this.selectedRecipientId)?.id ?? recipients[0]?.id;
      const nextRecipientId = next(recipients, (r) => r.id === selectedRecipientId)?.id;
      const prevRecipientId = prev(recipients, (r) => r.id === selectedRecipientId)?.id;
      const recipientIndex = recipients.findIndex((r) => r.id === selectedRecipientId);
      const recipientPreview = selectedRecipientId
        ? this.getRecipientPreview(d.documentId, selectedOrgNo, selectedRecipientId)
        : undefined;
      const incorporationPreview = incorporationCompanies
        ? this.getIncorporationCompanyPreview(d.documentId, selectedOrgNo)
        : undefined;
      const previews: DocumentPreview[] = isOk(recipientPreview)
        ? [recipientPreview.data]
        : isPending(recipientPreview)
          ? []
          : isOk(incorporationPreview)
            ? [incorporationPreview.data]
            : isPending(incorporationPreview)
              ? []
              : compact([
                  this.previews.find(
                    (p) => p.documentId === d.documentId && p.orgNo === selectedOrgNo,
                  ),
                ]);
      const signingStatus: DocumentTab["extraAction"] = ({
        documentId,
        orgNo,
      }: {
        documentId: string;
        orgNo?: string;
      }) => {
        const preview = previews.find((p) => p.documentId === documentId && p.orgNo === orgNo);
        const documentItem = this.projectContext.currentDocuments.find(
          (d) => d.documentId === documentId,
        );
        return when(
          documentItem &&
            preview &&
            isDocumentSentForSigning(this.projectContext, documentId) &&
            openSigningStatusDialogAction(documentItem, preview),
          (action) => [
            "link",
            {
              label: action.label,
              onClick: () => this.dispatch([action]),
            },
          ],
        );
      };
      const isPreviewInProgress = previews.some((p) => p.isPreviewInProgress);
      const versionToggle: DocumentTab["extraAction"] | undefined = previews.some(
        (p) => p.baseVersionContent,
      )
        ? () => [
            "toggle-buttons",
            [
              {
                variant: "basic",
                label: { key: "dbot.preview.current" },
                icon: "file-lock",
                selected: this.view.selectedDocumentPreviewBaseVersion,
                onClick: () => this.setViewState({ selectedDocumentPreviewBaseVersion: true }),
                disableMinWidth: true,
                disabled: isPreviewInProgress,
              },
              {
                variant: "basic",
                label: { key: "dbot.preview.revised" },
                icon: !isPreviewInProgress ? "file-dashed" : undefined,
                loading: isPreviewInProgress,
                tooltip: isPreviewInProgress ? { key: "dbot.preview-in-progress" } : undefined,
                selected: !this.view.selectedDocumentPreviewBaseVersion,
                onClick: () => this.setViewState({ selectedDocumentPreviewBaseVersion: false }),
                disableMinWidth: true,
              },
            ],
          ]
        : undefined;
      const isCustomDocBlob =
        d.form.type === DocumentType.custom_document && d.form.customDocumentType === "blob";

      const decisionAttachments = this.decisions
        .map((decision) => {
          return decision.attachments
            .filter((a) => a.selectedDocuments.includes(d.type))
            .map<UploadedAttachment>((a) => ({
              id: a.id,
              filename: a.filename,
              source: getDecisionName(decision.type, decision.decision.decisionData),
              documentConversionId: a.documentConversionId,
              isConverting: a.isConverting,
            }));
        })
        .flatMap((a) => a);
      const form = this.documents.find((doc) => doc.documentId === d.documentId)?.form;
      const documentContext = form && "context" in form ? form.context : undefined;
      return {
        type: d.type,
        content: !previews.length
          ? undefined
          : previews.map((p) => ({
              type: "document",
              content: this.view.selectedDocumentPreviewBaseVersion
                ? p.baseVersionContent
                : p.content,
              documentId: p.documentId,
              orgNo: p.orgNo,
              url: p.url,
              attachments: p.attachments,
            })),
        documentId: d.documentId,
        uploadedAttachments: [...d.attachments, ...decisionAttachments],
        uploadingAttachments: documentContext ? uploadingAttachments(documentContext) : undefined,
        title: getProjectDocumentTitle(d, this.projectContext.locale),
        recipientLabel: hasRecipientPreviews
          ? {
              key: "dbot.previewing-recipient",
              params: {
                n: recipientIndex + 1,
                m: recipients.length,
              },
            }
          : undefined,
        nextRecipient: nextRecipientId
          ? action(() => {
              this.selectedRecipientId = nextRecipientId;
            })
          : undefined,
        prevRecipient: prevRecipientId
          ? action(() => {
              this.selectedRecipientId = prevRecipientId;
            })
          : undefined,
        recipientSelector: recipients.length
          ? {
              value: selectedRecipientId,
              items: recipients.map((r) => r.id),
              idFn: identity,
              labelFn: (id) => recipients.find((r) => r.id === id)?.name ?? "",
              onSelect: action((id) => {
                this.selectedRecipientId = id;
              }),
            }
          : undefined,
        companyLabel:
          hasRecipientPreviews && !recipients.length
            ? { key: "dbot.no-recipients" }
            : companies.length > 1
              ? {
                  key: "dbot.previewing-company",
                  params: {
                    n: companyIndex + 1,
                    m: companies.length,
                  },
                }
              : undefined,
        nextCompany: nextCompany
          ? action(() => {
              this.selectedCompany = nextCompany;
            })
          : undefined,
        prevCompany: prevCompany
          ? action(() => {
              this.selectedCompany = prevCompany;
            })
          : undefined,
        companySelector:
          companies.length > 1
            ? {
                value: selectedOrgNo,
                items: companies.map((r) => r.orgNo),
                idFn: identity,
                labelFn: (orgNo) => companies.find((c) => c.orgNo === orgNo)?.name ?? "",
                onSelect: action((orgNo) => {
                  this.selectedCompany = orgNo;
                }),
              }
            : undefined,
        download: ({ documentId, orgNo }) => ({
          onDownload: () => {
            if (requiresSubscription(this.projectContext.subscriptionTypeModel)) {
              this.dispatch([showPaymentDialog]);
            } else {
              this.downloadDocument(
                documentId,
                orgNo,
                selectedRecipientId,
                this.view.selectedDocumentPreviewBaseVersion === true,
              ).then();
            }
          },
          documentType: d.type,
        }),
        downloadAttachment: ({ documentId, orgNo, type }) => ({
          onDownload: () => {
            if (requiresSubscription(this.projectContext.subscriptionTypeModel)) {
              this.dispatch([showPaymentDialog]);
            } else {
              this.downloadAutoAttachment(documentId, type, orgNo).then();
            }
          },
        }),
        upload: isCustomDocBlob
          ? action(({ documentId, orgNo }, files) =>
              this.uploadDocumentBlob(documentId, orgNo, files),
            )
          : undefined,
        uploadFileConfig: isCustomDocBlob
          ? toPdfOrOfficeUploadConfig(this.projectContext.fileUploadConfig)
          : undefined,
        isUploading: ({ documentId, orgNo }) =>
          this.customDocumentUpload.some((x) => x.documentId === documentId && x.orgNo === orgNo),
        isConverting: ({ documentId, orgNo }) =>
          this.customDocumentConverting.some(
            (x) =>
              x.documentId === documentId &&
              x.orgNo === orgNo &&
              isPending(x.model.conversionResult),
          ),
        extraAction: versionToggle ?? signingStatus,
      };
    });
  }

  initRecipientPreviewSync() {
    this.reactionDisposers.set(
      "recipient-preview",
      reaction(
        () => {
          const preview = this.sortedDocumentPreviews.find(
            (p) => p.documentId === this.selectedDocumentPreview,
          );
          const document = this.documents.find((d) => d.documentId === preview?.documentId);
          const companies = this.projectContext.companies;
          const selectedCompany =
            companies.find((c) => c.orgNo === this.selectedCompany)?.orgNo ?? companies[0].orgNo;
          const recipients =
            when(document, (d) =>
              getDocumentHandler(d.type).getRecipients?.(d.form, selectedCompany),
            ) ?? [];

          let selectedRecipientId: RecipientId | undefined;
          if (
            document?.form.type === DocumentType.custom_document &&
            document.form.context.documentData.customDocumentSendOption === "personalised"
          ) {
            selectedRecipientId =
              recipients?.find((r) => r.id === this.selectedRecipientId)?.id ?? recipients[0]?.id;
          }
          return [document?.documentId, selectedCompany, selectedRecipientId] as const;
        },
        ([documentId, selectedCompany, selectedRecipientId]) => {
          if (documentId && selectedCompany && selectedRecipientId) {
            this.updateRecipientPreview(documentId, selectedCompany, selectedRecipientId);
          }
        },
        { fireImmediately: true },
      ),
    );
  }

  updateRecipientPreview(
    documentId: string,
    selectedCompany: string,
    selectedRecipientId: RecipientId,
  ) {
    if (!this.hasRecipientPreview(documentId, selectedCompany, selectedRecipientId)) {
      this.setRecipientPreview(documentId, selectedCompany, selectedRecipientId, pending());
      this.http
        .get(
          `/project/${this.projectId}/preview-document/${documentId}?recipientId=${selectedRecipientId}&orgNo=${selectedCompany}`,
        )
        .then((res) => {
          this.setRecipientPreview(documentId, selectedCompany, selectedRecipientId, ok(res.data));
        })
        .catch(() => {
          this.setRecipientPreview(documentId, selectedCompany, selectedRecipientId, failure());
        });
    }
  }

  initIncorporationPreviewSync() {
    const incorporation = this.projectContext.jsonData.decisions.find(
      (d) => d.decisionData.type === DecisionType.incorporation,
    )?.decisionData as Incorporation | undefined;
    if (!incorporation?.companyNames?.length) return;

    this.reactionDisposers.set(
      "incorporation-preview",
      reaction(
        () => {
          const preview = this.sortedDocumentPreviews.find(
            (p) => p.documentId === this.selectedDocumentPreview,
          );
          const document = this.documents.find((d) => d.documentId === preview?.documentId);
          const selectedOrgNo = this.selectedCompany ?? "0";
          return [document?.documentId, selectedOrgNo] as const;
        },
        ([documentId, selectedOrgNo]) => {
          if (documentId) {
            this.updateIncorporationCompanyPreview(documentId, selectedOrgNo);
          }
        },
        { fireImmediately: true },
      ),
    );
  }

  updateIncorporationCompanyPreview(documentId: string, orgNo: string) {
    if (!this.getIncorporationCompanyPreview(documentId, orgNo)) {
      this.setIncorporationCompanyPreview(documentId, orgNo, pending());
      this.http
        .get(`/project/${this.projectId}/preview-document/${documentId}?orgNo=${orgNo}`)
        .then((res) => {
          this.setIncorporationCompanyPreview(documentId, orgNo, ok(res.data));
        })
        .catch(() => {
          this.setIncorporationCompanyPreview(documentId, orgNo, failure());
        });
    }
  }

  get documentTabs(): TabInPreview[] {
    let sorted = this.sortedDocumentPreviews;
    if (shouldAddReviewTab(this.documents, this.projectContext)) {
      sorted = this.withReviewTab(sorted);
    }
    if (shouldAddConductGeneralMeetingTab(this.projectContext)) {
      sorted = this.withConductElectronicGeneralMeetingTab(sorted);
    }
    return sorted;
  }

  get activeDocumentTab(): TabInPreview | undefined {
    return this.documentTabs.find((x) => x.documentId === this.selectedDocumentPreview);
  }

  private withReviewTab(sorted: TabInPreview[]): TabInPreview[] {
    const index = sorted.findIndex((d) => d.type === DocumentType.board_meeting);
    const documentId = sorted[index].documentId;
    return sorted.toSpliced(index, 0, {
      type: "review-matters",
      content: {
        type: "custom",
        content: (
          <ReviewMinutesAdminInitializer
            documentId={documentId}
            orgNo={this.orgNo}
            controller={this.reviewController}
          />
        ),
      },
      documentId: reviewDocumentId,
      title: { key: "review-matters" },
    });
  }

  private withConductElectronicGeneralMeetingTab(sorted: TabInPreview[]): TabInPreview[] {
    const index = sorted.findIndex((d) => d.type === DocumentType.notice_general_meeting);
    return sorted.toSpliced(index + 1, 0, {
      type: "conduct-general-meeting",
      content: {
        type: "custom",
        content: (
          <AdminElectronicGeneralMeetingInitializer
            projectId={this.projectContext.projectId}
            controller={this.electronicGMController}
          />
        ),
      },
      documentId: conductGeneralMeetingDocumentId,
      title: { key: "conduct-general-meeting" },
    });
  }

  downloadDocument(
    documentId: string,
    orgNo?: string,
    recipientId?: RecipientId,
    previewBaseVersion?: boolean,
  ): Promise<void> {
    const isIncorporation = this.projectContext.jsonData.decisions.some(
      (d) => d.decisionData.type === DecisionType.incorporation,
    );
    if (previewBaseVersion || recipientId || isIncorporation) {
      return downloadDocument(
        `/project/${this.projectId}/download-document/${documentId}${toQueryString({ orgNo, recipientId, previewBaseVersion })}`,
        this.http,
      );
    } else {
      return downloadDocument(
        `/document/view/pdf/${documentId}/${orgNo}?includeAttachments=true`,
        this.http,
      );
    }
  }

  downloadAutoAttachment(
    documentId: string,
    type: AutoAttachmentType,
    orgNo?: string,
  ): Promise<void> {
    return downloadDocument(
      `/project/${this.projectId}/download-auto-attachment/${documentId}/${type}${toQueryString({ orgNo })}`,
      this.http,
    );
  }

  setupDocument(
    document: Document,
    options?: { justAdded?: boolean },
    index?: number,
  ): DocumentAndForm {
    const context = createDocumentContext(document.documentId, this);
    const d: DocumentAndForm = {
      documentId: document.documentId,
      type: document.documentData.type,
      form: getDocumentHandler(document.documentData.type).initNewForm(
        this.dispatch,
        context,
        options,
      ),
      attachments: document.attachments ?? [],
      autoAttachments: context.autoAttachments,
    };
    if (index !== undefined && index !== -1) {
      this.documents[index] = d;
      return this.documents[index];
    } else {
      this.documents.push(d);
    }
    // Ensure to return the now observed document instead of `d` directly
    return this.documents.find((d) => d.documentId === document.documentId)!;
  }

  isDocumentLocked(documentId: string): boolean {
    return isDocumentLocked(this, documentId);
  }

  confirmRemoveDocument(event: ConfirmRemoveDocumentEvent) {
    if (this.state.type !== "removeDocument") {
      return;
    }
    const documentId = this.state.documentId;
    this.cleanupDocument(documentId);
    removeDocument(this.documents, documentId);
    this.save([{ type: "document-removed", documentId }]);
    this.dispatch(event.onSuccess);
  }

  get isSaving() {
    return this.currentSaveQueueItems.length > 0;
  }

  save(
    changes: ProjectChange[],
    onDone?:
      | (DbotEvent | DCEvent)[]
      | ((err: unknown, result?: UpdateProjectResponse) => (DbotEvent | DCEvent)[]),
  ) {
    if (this.isReadonly || !changes.length) {
      return;
    }
    logDebug(
      "ProjectController::save: scheduling",
      changes.map((i) => i.type),
    );
    this.clearValidationResult();
    if (onDone) {
      if (Array.isArray(onDone)) {
        this.onSaveDone.push(...onDone);
      } else {
        this.onSaveDone.push(onDone);
      }
    }
    this.saveQueue.push(...changes);
  }

  handleSaveError(err: unknown) {
    if (isHttpError(HttpStatus.CONFLICT, err)) {
      this.dispatch([showWarning(() => <ConflictMessage dispatch={this.dispatch} />, {})]);
    } else {
      this.dispatch([showError({ key: "dbot.saveProjectError" })]);
    }
  }

  *doSave(changes: ProjectChange[], done: (err: unknown, result?: UpdateProjectResponse) => void) {
    logDebug("ProjectController::doUpdateProject");
    const body: UpdateProjectRequest = {
      projectVersion: this.version,
      changes,
    };
    try {
      const res: AxiosResponse<UpdateProjectResponse> = yield this.http.post(
        `/project/${this.projectId}`,
        body,
      );
      const existingProject = this.project;
      this.setProject(res.data.project);

      const newDecisions = res.data.addedDecisionIds.length
        ? res.data.project.jsonData.decisions.filter((d) =>
            res.data.addedDecisionIds.includes(d.decisionId),
          )
        : [];
      const newDocuments = res.data.addedDocumentIds.length
        ? res.data.project.jsonData.documents.filter((d) =>
            res.data.addedDocumentIds.includes(d.documentId),
          )
        : [];
      const existingDocuments = existingProject.jsonData.documents;

      newDecisions.forEach((newDecision) => {
        const index = res.data.project.jsonData.decisions.findIndex(
          (d) => d.decisionId === newDecision.decisionId,
        );
        this.initDecision(newDecision, index);
      });
      newDocuments.forEach((newDocument) => {
        this.initDocument(this.setupDocument(newDocument, { justAdded: true }));
      });

      res.data.project.jsonData.decisions
        .filter((d) => res.data.changedDecisionIds?.includes(d.decisionId))
        .forEach((changedDecision) => this.reinitDecision(changedDecision));

      res.data.project.jsonData.documents
        .filter((d) => res.data.changedDocumentIds.includes(d.documentId))
        .forEach((updatedDoc) => {
          const existing = this.documents.find((d) => d.documentId === updatedDoc.documentId);
          if (!existing) {
            return;
          }
          this.cleanupDocument(updatedDoc.documentId);
          const index = this.documents.findIndex((x) => x.documentId === updatedDoc.documentId);
          if (index !== -1) {
            this.setupDocument(updatedDoc, undefined, index);
            this.initDocument(this.documents[index]);
          }
        });

      const syncedDocuments = res.data.project.jsonData.documents.filter(
        (d) => !this.documents.some((existing) => existing.documentId === d.documentId),
      );
      this.syncDocumentsFromProject(res.data.project);

      if (
        [...newDocuments, ...syncedDocuments].some(
          (d) =>
            d.documentData.type === "notice_general_meeting" ||
            d.documentData.type === "general_meeting",
        )
      ) {
        this.generalMeetingFields = this.initGeneralMeetingFields();
      }

      if (newDocuments.length) {
        this.dispatch({
          type: "dbot/documents-added",
          addedDocumentTypes: newDocuments.map((d) => d.documentData.type),
          existingDocumentTypes: existingDocuments.map((d) => d.documentData.type),
        });
      }

      this.dispatch(
        newDecisions.map((d) => ({
          type: "dbot/decision-added",
          decisionId: d.decisionId,
          isFreetext: d.decisionData.type === DecisionType.freetext,
        })),
      );

      // TODO Do this as part of onDone instead
      const addedDecision = decisionToOpen(
        newDecisions.map((d) => ({ decisionId: d.decisionId, type: d.decisionData.type })),
      );
      if (addedDecision && addedDecision.type !== DecisionType.freetext) {
        this.dispatch({
          type: "dbot/open-details-panel",
          panel: {
            type: "decision",
            decisionId: addedDecision.decisionId,
            documentId: changes.find(
              (c) => c.type === "decision-added" || c.type === "decisions-copied",
            )?.documentId,
          },
        });
      }

      this.dispatch({
        type: "dbot/decisions-saved",
        saved: res.data.project.jsonData.decisions,
      });
      this.dispatch({ type: "dbot/save-project-success" });
      done(null, res.data);

      // TODO Move this to BEFORE adding the decisions
      const missingDocs = getMissingDocuments(existingProject, res.data.project);
      if (missingDocs.length) {
        this.dispatch(
          confirmAction({
            title: { key: "dbot.confirm.add-required-documents.title" },
            text: {
              key: "dbot.confirm.add-required-documents.text",
              params: { documents: { type: "md-list", value: missingDocs.map(getDocumentLabel) } },
            },
            confirmLabel: { key: "yes" },
            cancelLabel: { key: "no" },
            onConfirm: [
              thunk(() =>
                this.dispatch([{ type: "dbot/add-documents", documentTypes: missingDocs }]),
              ),
            ],
          }),
        );
      }
    } catch (err) {
      logDebug("err", err);
      this.handleSaveError(err);
      done(err);
    }
  }

  pollProjectUpdates(project: GetProjectResponse) {
    const onSuccess = action((res: HttpResponse<ProjectStatusResponse>): DCEvent[] => {
      //TODO: display success message.
      //TODO: update automatically should respect the success/failure
      if (res.data.type === "outOfDate") {
        if (!res.data.pollStatus) {
          this.dispatch({
            type: "dbot/update-document-preview",
            updatedPreviews: res.data.reason.updatedPreviews,
          });
        }
        this.project.version = res.data.newVersion;
        if (res.data.pollStatus) {
          fetchProjectStatus();
        } else {
          return [showSuccess({ key: "dbot.aoa-update.success" }, { autoHide: true })];
        }
      } else {
        fetchProjectStatus();
      }
      return [];
    });

    const fetchProjectStatus = () =>
      setTimeout(
        () =>
          this.dispatch(
            projectApi.pollProjectStatus({
              projectId: this.project.projectId,
              version: this.project.version,
              onSuccess,
              onFailure: () => {
                return [showGeneralError()];
              },
            }),
          ),
        3_000,
      );
    if (isAoAUpdateInProgress(project.aoaMetadata)) {
      fetchProjectStatus();
    }
  }

  pollIncorporationStatus() {
    this.reactionDisposers.get("incorporation-status-poll")?.();
    const poll = () => {
      const pending = incorporationDecisionsPendingBrReview(this.project.followUps);
      logDebug("ProjectController::pollIncorporationStatus", {
        pending,
        sendToBR: this.project.followUps.map((decisionFollowUp) => ({
          decisionId: decisionFollowUp.decisionId,
          followUps: decisionFollowUp.followUps
            .map((followUp) => followUp.data)
            .filter((data): data is SendToBRFollowUp => data.type === FollowUpType.sendToBR)
            .map((data) => ({
              brStatus: data.brStatus,
              hasDseAck: parseDseSubmitAck(data.brResultData) !== undefined,
            })),
        })),
      });
      pending.forEach((decisionId) => this.dispatch(checkIncorporationStatusEvent(decisionId)));
    };
    poll();
    const intervalId = setInterval(poll, INCORPORATION_STATUS_POLL_INTERVAL_MS);
    this.reactionDisposers.set("incorporation-status-poll", () => clearInterval(intervalId));
  }

  initEditDecision() {
    this.cleanupEditDecision();
    const detailsPanel = this.detailsPanel;
    if (detailsPanel?.type !== "decision") {
      return;
    }
    const orgNo = detailsPanel.orgNo ?? this.orgNo;
    const decisionId = detailsPanel.decisionId;
    const decision = this.decisions.find((d) => d.decisionId === decisionId);
    if (!decision) {
      return;
    }
    const decisionType = decision.type;

    if (orgNo === this.orgNo) {
      this.editedDecision = {
        decisionId,
        decisionType,
        orgNo,
        decision,
      };
    } else {
      this.editedDecision = {
        decisionId,
        decisionType,
        orgNo,
        decision: {
          ...decision,
          form: getHandler(decision.type).initNewForm(
            this.dispatch,
            createDecisionContext(
              decisionId,
              decision.decisionTexts,
              decision.decisionIntroTexts,
              decision.attachments,
              this,
              orgNo,
            ),
            decision.decision.decisionData,
            decision.decision,
          ),
        },
      };
    }
  }

  cleanupEditDecision() {
    this.editedDecision = undefined;
  }

  cleanupEditDocument() {
    this.documents.map((d) => {
      if (d.form.type === DocumentType.general_meeting) {
        d.form.validateMessageError = [];
      }
    });
  }

  isDocumentPanelExpanded(documentId: string) {
    return this.selectedDocumentId === documentId;
  }

  addEventListener(listener: (event: DbotEvent) => void): () => void {
    this.eventListeners.push(listener);
    return () => {
      this.removeEventListener(listener);
    };
  }

  removeEventListener(listener: (event: DbotEvent) => void) {
    const index = this.eventListeners.indexOf(listener);
    if (index >= 0) {
      this.eventListeners.splice(index, 1);
    }
  }

  readonly decisionsForValidation: DecisionType[] = [
    DecisionType.acquireOwnSharesPowerOfAttorney,
    DecisionType.approveAnnualAccount,
    DecisionType.approveCompanyBoardRemuneration,
    DecisionType.approveNominationCommitteeRemuneration,
    DecisionType.assignsNewCompanyBoard,
    DecisionType.auditor,
    DecisionType.accountant,
    DecisionType.capitalIncreaseThroughBonusIssue,
    DecisionType.changeBusinessPurpose,
    DecisionType.changeOfAddress,
    DecisionType.changeOfBusinessManager,
    DecisionType.changeArticlesOfAssociation,
    DecisionType.companyName,
    DecisionType.conversionOfShares,
    DecisionType.convertibleLoanPowerOfAttorney,
    DecisionType.dividend,
    DecisionType.dividendPowerOfAttorney,
    DecisionType.electNominationCommittee,
    DecisionType.electionOfCompanyBoard,
    DecisionType.groupContributionByGeneralMeeting,
    DecisionType.incorporation,
    DecisionType.increaseOfShareCapital,
    DecisionType.interimBalanceSheet,
    DecisionType.liquidateCompany,
    DecisionType.increaseShareCapitalPowerOfAttorney,
    DecisionType.merger,
    DecisionType.reductionOfShareCapital,
    DecisionType.noAuditor,
    DecisionType.noManagingDirector,
    DecisionType.shareTransferConsent,
    DecisionType.optionGrantLetter,
    DecisionType.optionExercise,
    DecisionType.splitShare,
    DecisionType.boardAssignsSignature,
  ];

  private validateProject({
    onSuccess,
    contextDocumentId,
  }: {
    onSuccess: DbotEvent;
    contextDocumentId?: string;
  }) {
    this.prekontrollFailure = undefined;
    this.invalidDecisions = new Set(
      this.decisions
        .filter(
          (decision) =>
            this.decisionsForValidation.includes(decision.type) &&
            !getHandler(decision.type).validate(decision.form),
        )
        .map((decision) => decision.decisionId),
    );

    this.invalidDocuments = new Set(
      this.documents
        .filter((d) => !(getDocumentHandler(d.type).validate?.(d.form) ?? true))
        .map((d) => d.documentId),
    );
    if (
      !isEmpty(this.invalidDecisions) ||
      (contextDocumentId !== undefined && this.invalidDocuments.has(contextDocumentId))
    ) {
      return;
    }

    // Incorporation: before the document goes out for signing, run BR's prekontroll so any
    // rejection surfaces now rather than after everyone has signed. Hold the success action
    // until prekontroll passes (see "dbot/prekontroll-incorporation-result").
    const incorporation = this.decisions.find((d) => d.type === DecisionType.incorporation);
    if (incorporation !== undefined && contextDocumentId !== undefined) {
      this.pendingSendForSigningPrekontroll = {
        decisionId: incorporation.decisionId,
        contextDocumentId,
        onSuccess,
      };
      // Drive the send-for-signing button spinner off the existing documentSigners pending state
      // while BR's prekontroll runs; the result handler clears or continues it.
      this.documentSigners.set(contextDocumentId, pending());
      this.dispatch(prekontrollIncorporationEvent(this.orgNo, incorporation.decisionId));
      return;
    }

    this.dispatch(onSuccess);
  }

  clearValidationResult() {
    this.invalidDecisions = new Set();
    this.invalidDocuments = new Set();
    this.prekontrollFailure = undefined;
  }
}

function forwardEvent(
  event: DbotEvent,
  decisions: DecisionAndForm<DecisionType>[],
  documents: DocumentAndForm[],
  summaryController: SummaryController,
  electronicGeneralMeetingController: AdminElectronicGeneralMeetingController,
  eventListeners: Array<(event: DbotEvent) => void>,
) {
  for (const d of decisions) {
    const h = getHandler(d.type);
    if (h.handleEvent) {
      h.handleEvent(d.form, event);
    }
  }
  for (const d of documents) {
    const h = getDocumentHandler(d.type);
    if (h.handleEvent) {
      h.handleEvent(d.form, event);
    }
  }
  summaryController.handleEvent(event);
  electronicGeneralMeetingController.handleEvent(event);
  for (const l of eventListeners) {
    l(event);
  }
}

function toDecisionItem(
  d: DecisionAndForm<DecisionType>,
  hasValidationError: boolean,
  includeInGeneralMeeting: boolean,
  defaultGeneralMeetingVotingType: GeneralMeetingVotingType,
  isApprovedByGeneralMeeting: boolean,
): DecisionItem {
  return {
    decisionId: d.decisionId,
    title: getDecisionActionLabel(d.type, d.decision.decisionData),
    type: d.type,
    decisionTexts: d.decisionTexts ?? [],
    decisionIntroTexts: d.decisionIntroTexts,
    attachments: d.attachments,
    decisionData: d.decision?.decisionData,
    includeInGeneralMeeting,
    defaultGeneralMeetingVotingType,
    isApprovedByGeneralMeeting,
    hasValidationError,
  };
}

function replaceLocalizedTextForDecision(
  data: LocalizedTextForDocument,
  decision: DecisionAndForm<DecisionType>,
) {
  const index = decision.decisionTexts.findIndex(
    (t) => t.documentType === data.documentType && t.language === data.language,
  );
  if (index > -1) {
    decision.decisionTexts[index] = data;
  } else {
    decision.decisionTexts.push(data);
  }
}

function replaceLocalizedIntroTextForDecision(
  data: LocalizedTextForDocument,
  decision: DecisionAndForm<DecisionType>,
) {
  const index = decision.decisionIntroTexts.findIndex(
    (t) => t.documentType === data.documentType && t.language === data.language,
  );
  if (index > -1) {
    decision.decisionIntroTexts[index] = data;
  } else {
    decision.decisionIntroTexts.push(data);
  }
}

export function getUpdatedRoles(
  roles: CompanyRole[],
  updates: UpdateBoardMeetingRecipientItem[],
): UpdateCompanyRoleItem[] {
  const optionsById = new Map<string | number, CompanyRole>(roles.map((o) => [o.companyroleId, o]));
  return updates.flatMap((u) => {
    const option = optionsById.get(u.id);
    if (option?.companyroleId) {
      return [{ companyroleId: option.companyroleId, email: u.email }];
    }
    return [];
  });
}

function isDecisionLocked(
  { projectContext, editedDecision, detailsPanel }: ProjectController,
  decisionId: string,
): boolean {
  const documentId = detailsPanel?.type === "decision" ? detailsPanel.documentId : undefined;
  return (
    projectContext.isLocked ||
    (editedDecision !== undefined &&
      editedDecision.decisionId === decisionId &&
      documentId !== undefined &&
      isDocumentLocked(projectContext, documentId))
  );
}

export function createDecisionContext(
  decisionId: string,
  decisionTexts: LocalizedTextForDocument[],
  decisionIntroTexts: LocalizedTextForDocument[],
  attachments: ProjectDecisionAttachment[],
  controller: ProjectController,
  orgNo?: string,
): DecisionContext<DecisionData> {
  const projectContext = controller.projectContext;
  return makeAutoObservable({
    decisionId,
    decisionTexts,
    decisionIntroTexts,
    get decisionData() {
      return controller.project.jsonData.decisions.find((d) => d.decisionId === decisionId)!
        .decisionData;
    },
    get boardMeetingFields() {
      return projectContext.boardMeetingFields;
    },

    get boardMeetingNoticeRecipients() {
      return projectContext.boardMeetingNoticeRecipients;
    },

    get currentDecisions() {
      return projectContext.currentDecisions;
    },

    get currentDocuments() {
      return projectContext.currentDocuments;
    },

    get currentSubscription() {
      return projectContext.currentSubscription;
    },

    get documentLanguages() {
      return projectContext.documentLanguages;
    },

    get dissolutionStatus() {
      return projectContext.dissolutionStatus;
    },

    get documentStatuses() {
      return projectContext.documentStatuses;
    },

    get generalMeetingFields() {
      return projectContext.generalMeetingFields;
    },

    get isLocked() {
      return isDecisionLocked(controller, decisionId);
    },

    get orgNo() {
      return orgNo ?? projectContext.orgNo;
    },

    get projectId() {
      return projectContext.projectId;
    },

    get signingBoardMembers() {
      return projectContext.signingBoardMembers;
    },

    get subscriptionTypeModel() {
      return projectContext.subscriptionTypeModel;
    },

    get fileUploadConfig() {
      return projectContext.fileUploadConfig;
    },

    get user() {
      return projectContext.user;
    },

    get contextData() {
      return projectContext.contextData;
    },

    get initialState() {
      return this.contextData[this.orgNo].initialState;
    },

    get inputStates(): InputState[] {
      return projectContext.contextData[this.orgNo].inputStates;
    },

    get inputState() {
      return findStateForDecision(this.inputStates ?? [], decisionId);
    },

    get aiClient(): AIClient {
      return projectContext.aiClient;
    },

    get projectStatus(): ProjectStatus {
      return projectContext.projectStatus;
    },

    get isSharepointEnabled(): boolean {
      return projectContext.isSharepointEnabled;
    },

    get locale() {
      return projectContext.locale;
    },

    get detailsPanelMaximized() {
      return projectContext.detailsPanelMaximized;
    },

    get boardMeetingVotes() {
      return projectContext.boardMeetingVotes;
    },

    get displayMode() {
      return projectContext.displayMode;
    },

    get participationList() {
      return projectContext.participationList;
    },

    get powerOfAttorneys() {
      return projectContext.powerOfAttorneys;
    },

    get availableCompanies() {
      return projectContext.availableCompanies;
    },

    get companies() {
      return projectContext.companies;
    },

    get isBatchProject() {
      return projectContext.isBatchProject;
    },

    get isMainCompany() {
      return this.orgNo === projectContext.orgNo;
    },

    get documentSigners() {
      return projectContext.documentSigners;
    },

    get userPrefs() {
      return projectContext.userPrefs;
    },

    get state() {
      return projectContext.state;
    },

    get jsonData() {
      return projectContext.jsonData;
    },

    get isProd() {
      return projectContext.isProd;
    },

    get featureToggles() {
      return projectContext.featureToggles;
    },

    get subscribers() {
      return projectContext.subscribers;
    },

    getUploadingDocumentBlob(documentId: string, orgNo: string) {
      return projectContext.getUploadingDocumentBlob(documentId, orgNo);
    },

    get attachments() {
      return (
        controller.decisions.find((d) => d.decisionId === decisionId)?.attachments ?? attachments
      );
    },
    get aoaMetadata() {
      return controller.project.aoaMetadata;
    },
  });
}

function createDocumentContext(
  documentId: string,
  ctrl: ProjectController,
): DocumentContext<DocumentData> {
  return {
    documentId,

    get documentData() {
      return ctrl.project.jsonData.documents.find((d) => d.documentId === documentId)!.documentData;
    },

    get documentOverrides() {
      return mapValues(ctrl.project.companyData, (data) => data.documentOverrides[documentId]);
    },

    get boardMeetingFields() {
      return ctrl.projectContext.boardMeetingFields;
    },

    get boardMeetingNoticeRecipients() {
      return ctrl.projectContext.boardMeetingNoticeRecipients;
    },

    get currentDecisions() {
      return ctrl.projectContext.currentDecisions;
    },

    get currentDocuments() {
      return ctrl.projectContext.currentDocuments;
    },

    get currentSubscription() {
      return ctrl.projectContext.currentSubscription;
    },

    get documentLanguages() {
      return ctrl.projectContext.documentLanguages;
    },

    get documentStatuses() {
      return ctrl.projectContext.documentStatuses;
    },

    get generalMeetingFields() {
      return ctrl.projectContext.generalMeetingFields;
    },

    get isProjectLocked() {
      return ctrl.projectContext.isLocked;
    },

    get isLocked() {
      return isDocumentLocked(ctrl.projectContext, documentId);
    },

    get orgNo() {
      return ctrl.projectContext.orgNo;
    },

    get projectId() {
      return ctrl.projectContext.projectId;
    },

    get signingBoardMembers() {
      return ctrl.projectContext.signingBoardMembers;
    },

    get subscriptionTypeModel() {
      return ctrl.projectContext.subscriptionTypeModel;
    },

    get fileUploadConfig() {
      return ctrl.projectContext.fileUploadConfig;
    },

    get user() {
      return ctrl.projectContext.user;
    },

    get contextData() {
      return ctrl.projectContext.contextData;
    },

    get initialState() {
      return ctrl.projectContext.initialState;
    },

    get inputStates(): InputState[] {
      return ctrl.projectContext.inputStates;
    },

    get aiClient(): AIClient {
      return ctrl.projectContext.aiClient;
    },

    get projectStatus(): ProjectStatus {
      return ctrl.projectContext.projectStatus;
    },

    get isSharepointEnabled(): boolean {
      return ctrl.projectContext.isSharepointEnabled;
    },

    get locale() {
      return ctrl.projectContext.locale;
    },

    get detailsPanelMaximized() {
      return ctrl.projectContext.detailsPanelMaximized;
    },

    get boardMeetingVotes() {
      return ctrl.projectContext.boardMeetingVotes;
    },

    get displayMode() {
      return ctrl.projectContext.displayMode;
    },

    get participationList() {
      return ctrl.projectContext.participationList;
    },

    get powerOfAttorneys() {
      return ctrl.projectContext.powerOfAttorneys;
    },

    get availableCompanies() {
      return ctrl.projectContext.availableCompanies;
    },

    get companies() {
      return ctrl.projectContext.companies;
    },

    get isBatchProject() {
      return ctrl.projectContext.isBatchProject;
    },

    get documentSigners() {
      return ctrl.projectContext.documentSigners;
    },

    get userPrefs() {
      return ctrl.projectContext.userPrefs;
    },

    get state() {
      return ctrl.projectContext.state;
    },

    get jsonData() {
      return ctrl.projectContext.jsonData;
    },

    get isProd() {
      return ctrl.projectContext.isProd;
    },

    get featureToggles() {
      return ctrl.projectContext.featureToggles;
    },

    get subscribers() {
      return ctrl.projectContext.subscribers;
    },

    getUploadingDocumentBlob(documentId: string, orgNo: string) {
      return ctrl.projectContext.getUploadingDocumentBlob(documentId, orgNo);
    },

    get attachments(): UploadedAttachmentProps[] {
      const documentAndForm = ctrl.documents.find((d) => d.documentId === documentId)!;
      const decisionAttachments: UploadedAttachment[] = this.currentDecisions
        .flatMap((d) =>
          d.attachments.map((a) => ({
            ...a,
            source: getDecisionName(d.type, d.decisionData),
            decisionId: d.decisionId,
          })),
        )
        .filter((a) => a.selectedDocuments.includes(documentAndForm.type));
      return [...documentAndForm.attachments, ...decisionAttachments];
    },

    get autoAttachments(): AttachmentPreview[] {
      const content = ctrl.sortedDocumentPreviews.find((p) => p.documentId === documentId)?.content;
      if (isArray(content)) {
        return content.flatMap((c) => c.attachments ?? []);
      }
      return [];
    },
  };
}

export const getProjectMenuItems = (
  summaryController: SummaryController,
  dispatch: DbotEventDispatcher,
  projectName: string,
  t: TFunction,
): DropdownMenuItems =>
  compact([
    { type: "label", label: t("project") },
    {
      type: "item",
      label: t("duplicate"),
      icon: "copy",
      onSelect: () =>
        dispatch({
          type: "dbot/duplicate-project",
          name: t("dbot.duplicate.projectName", { name: projectName }),
        }),
    },
    {
      type: "item",
      label: t("delete"),
      icon: "trash",
      color: "error",
      onSelect: () => dispatch({ type: "dbot/delete-project" }),
      isDisabled: anyDocumentSentForSigning(summaryController.context.documentStatuses),
      tooltip: anyDocumentSentForSigning(summaryController.context.documentStatuses)
        ? t("dbot.projectList.deleteDisabled")
        : undefined,
    },
    { type: "separator" },
    {
      type: "item",
      icon: "chat-dots",
      label: t("give-us-feedback"),
      onSelect: () => {
        openLinkInNewWindow("https://airtable.com/apppdnKJrnENisz3z/shrKU5vSljC5QZs3j");
      },
    },
  ]);

export const documentStatusTag = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): TagProps =>
  isDocumentLocked(context, documentId)
    ? {
        icon: "lock",
        label: { key: "dbot.locked" },
        color: "neutral",
        tooltip: { key: "dbot.documentLocked" },
      }
    : {
        icon: "pen",
        label: { key: "draft" },
        color: "neutral",
      };

export const getTabLabel = (tab: TabId): TMessage => {
  switch (tab) {
    case "companies":
      return { key: "dbot.companies" };
    case "documents":
      return { key: "dbot.documents" };
    case "follow-up":
      return { key: "dbot.follow-up" };
    case "matters":
      return { key: "dbot.matters" };
    case "meetings":
      return { key: "header.meetings" };
  }
};

export function getVisibleTabs({
  decisions,
  decisionTypes,
  documents,
  locale,
  conductElectronicGeneralMeeting,
  displayMode,
  isBatchProject,
  hasMergerWithCounterpart,
}: {
  decisions: DecisionListItem[];
  decisionTypes: DecisionType[];
  documents: DocumentAndForm[];
  locale: Language;
  conductElectronicGeneralMeeting?: boolean;
  displayMode?: "fullscreen" | "screen-share" | "normal" | "overlay";
  isBatchProject?: boolean;
  hasMergerWithCounterpart?: boolean;
}): TabId[] {
  if (displayMode === "screen-share") {
    return [];
  }
  return compact([
    (isBatchProject || hasMergerWithCounterpart) && "companies",
    showMattersTab(decisionTypes, documents) && "matters",
    "documents",
    conductElectronicGeneralMeeting && "meetings",
    hasFollowUpDecisions(decisions, locale) && "follow-up",
  ]);
}

export function showMattersTab(
  decisionTypes: DecisionType[],
  documents: DocumentAndForm[],
): boolean {
  return documents.length !== 1 || decisionTypes.some((type) => type !== DecisionType.freetext);
}

export const documentSendingOrSigningStatusTag = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): TagProps | undefined =>
  isDocumentSigned(context, documentId)
    ? {
        icon: "signature",
        label: { key: "dbot.summary.signingStatus.signed" },
        color: "success",
      }
    : isDocumentSentForSigning(context, documentId)
      ? {
          icon: "pen",
          label: { key: "sent-for-signing" },
          color: "success",
        }
      : isDocumentSentForReview(context, documentId)
        ? { icon: "paper-plane", label: { key: "sent-for-review" }, color: "info" }
        : isDocumentSentForVoting(context, documentId)
          ? { icon: "paper-plane", label: { key: "sent-for-voting" }, color: "info" }
          : isDocumentSent(context, documentId)
            ? { icon: "paper-plane", label: { key: "sent" }, color: "success" }
            : undefined;

function hasFollowUpDecisions(decisions: DecisionListItem[], locale: Language): boolean {
  return decisions.some((d) => getDecisionFollowUpPanels(d, locale).length > 0);
}

export const reviewDocumentId = "review-matters-id";
export const conductGeneralMeetingDocumentId = "conduct-general-meeting-id";

export function findBoardMeetingMinutes(jsonData: ProjectJsonData) {
  return jsonData.documents.find((f) => f.documentData.type === DocumentType.board_meeting)
    ?.documentId;
}

const boardMeetingOnlyDecisionTypes: ReadonlySet<DecisionType> = new Set([
  DecisionType.changeOfAddress,
  DecisionType.accountant,
  DecisionType.changeOfBusinessManager,
  DecisionType.noBusinessManager,
]);

export const isDecisionExcludedInDocument = (
  decisionItem: DecisionItem,
  documentType: DocumentType,
): boolean => {
  if (decisionItem.decisionData?.type === DecisionType.freetext) {
    return !showFreetext(decisionItem.decisionData, documentType);
  }
  if (
    documentType === DocumentType.notice_general_meeting ||
    documentType === DocumentType.general_meeting
  ) {
    return (
      decisionItem.decisionData !== undefined &&
      boardMeetingOnlyDecisionTypes.has(decisionItem.decisionData.type)
    );
  }
  return false;
};

export const getMissingDocuments = (
  existingProject: GetProjectResponse,
  updatedProject: GetProjectResponse,
): DocumentType[] => {
  const gmTypes = [DocumentType.notice_general_meeting, DocumentType.general_meeting];
  const requiresGm =
    updatedProject.decisionsRequiresGeneralMeeting.length >
    existingProject.decisionsRequiresGeneralMeeting.length;
  const requiresAoa =
    updatedProject.decisionsRequiringArticlesOfAssociation.length >
    existingProject.decisionsRequiringArticlesOfAssociation.length;
  const containsGm = updatedProject.jsonData.documents.some((d) =>
    gmTypes.includes(d.documentData.type),
  );
  const containsAoa = updatedProject.jsonData.documents.some(
    (d) => d.documentData.type === DocumentType.articlesofassociation,
  );

  return [
    ...(requiresGm && !containsGm ? gmTypes : []),
    ...(requiresAoa && !containsAoa ? [DocumentType.articlesofassociation] : []),
  ];
};

export const getProjectDocumentTitle = (d: DocumentAndForm, lang: Language): TMessage =>
  getDocumentHandler(d.type)?.getTitle?.(d.form, lang) ?? getDocumentLabel(d.type);

export function sortDocuments(documents: Document[]): Document[] {
  const sortValues: { [key in DocumentType]: number } = Object.values(DocumentType).reduce(
    (acc, d, i) => {
      acc[d] = (i + 1) * 100;
      return acc;
    },
    {} as { [key in DocumentType]: number },
  );

  const customDocumentTypeOrder: CustomDocumentType[] = [
    "boardStatementDebtConversion",
    "setOffDeclaration",
    "boardStatementContributionInKind",
  ];

  return sortBy(documents, (d) => {
    if (d.documentData.type === DocumentType.custom_document) {
      if (d.documentData.customDocumentType === "mergerPlan") {
        return 0;
      }
      if (d.documentData.customDocumentType === "mergerShareholderNotice") {
        return 0.5;
      }
      if (
        d.documentData.customDocumentType &&
        customDocumentTypeOrder.includes(d.documentData.customDocumentType)
      ) {
        const order = customDocumentTypeOrder.indexOf(d.documentData.customDocumentType) + 1;
        return sortValues[DocumentType.board_meeting] + order;
      }
    }
    return sortValues[d.documentData.type];
  });
}
